import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import {
  formatDateInParis,
  formatDateTimeInParis,
  formatMonthYearInParis,
  getDatePartsInParis,
  toDateInputValueInParis,
} from '../utils/dateTime';
import './AdminDashboard.css';
import './UserDashboard.css';

const apiBaseUrl = axios.defaults.baseURL || '';
const resolveMediaUrl = (path) => {
  if (!path) return null;
  const raw = String(path).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw)) return raw;
  const base = String(apiBaseUrl || '').replace(/\/$/, '');
  const suffix = raw.startsWith('/') ? raw : `/${raw}`;
  return base ? `${base}${suffix}` : suffix;
};

const houseParisSubRegions = [
  'PARIS',
  'Palaiseau',
  'Serris'
];

const houseParisResidencesBySubRegion = {
  PARIS: ['Nexity Studea Daumesnil', 'Nexity Studea Jean Cocteau', 'Nexity Studea Paris Bastille', 'Nexity Studea Paris Davout', 'Nexity Studea Paris Grande Bibliotheque', 'Nexity Studea Rive Gauche'],
  Palaiseau: ['Nexity Studea Palaiseau Centre'],
  Serris: ['Nexity Studea Val D Europe 1', "Nexity Studea Val D'europe 2"]
};

const houseResidencesByCity = {
  'Aix En Provence': ['Nexity Studea Aix Centre', 'Nexity Studea Aix Galice', 'Nexity Studea Republique'],
  Amiens: ['Nexity Studea Amiens Centre'],
  Angers: ['Nexity Studea Saint-serge'],
  Bagnolet: ['Nexity Studea Bagnolet'],
  Bordeaux: ['Nexity Studea Bordeaux Centre 1', 'Nexity Studea Bordeaux Centre 2', 'Nexity Studea Bordeaux Centre 3', 'Nexity Studea Bordeaux Chartrons', 'Nexity Studea Bordeaux Bastide', 'Nexity Studea Bordeaux West'],
  Bron: ['Nexity Studea Porte Des Alpes 2'],
  Cenon: ['Nexity Studea Bordeaux Bastide'],
  Cergy: ['Nexity Studea Cergy Universite 1', 'Nexity Studea Cergy Universite 2'],
  Chelles: ['Nexity Studea Chelles Gare RER'],
  'Clermont-ferrand': ['Nexity Studea Clermont 1'],
  'Joinville Le Pont': ['Nexity Studea Joinville Center'],
  Lille: ['Nexity Studea Lille Centre', 'Nexity Studea Lille Scientific City', 'Nexity Studea Lille I', 'Nexity Studea Moulin', 'Nexity Studea Novalille'],
  Lyon: ['Nexity Studea Carre Des Halles', 'Nexity Studea Grenoble Centre', 'Nexity Studea Louis Weil', 'Nexity Studea Claude Bernard 2', 'Nexity Studea Garibaldi Berthelot', 'Nexity Studea Jean Jaures 2', 'Nexity Studea Jean Mace', 'Nexity Studea Lyon Confluence', 'Nexity Studea Lyon Ouest 2', 'Nexity Studea Lyon Vaise', 'Nexity Studea Massena 1', 'Nexity Studea Massena 2', 'Nexity Studea Porte Des Alpes 2', "Nexity Studea Presqu'ile", 'Nexity Studea Saxe-Gambetta', 'Nexity Studea Universite 1', 'Nexity Studea Charpennes 1', 'Nexity Studea La Doua', 'Nexity Studea Montchat', 'Nexity Studea Villeurbanne Republique 1', 'Nexity Studea Villeurbanne Republique 2'],
  Marseille: ['Nexity Studea Euromediterranee', 'Nexity Studea Euromediterranee 2', 'Nexity Studea Marseille Capelette', "Nexity Studea Marseille Croix D'azur", 'Nexity Studea Marseille Ferrari', 'Nexity Studea Marseille Timone'],
  Merignac: ['Nexity Studea Bordeaux West'],
  Montpellier: ['Nexity Studea Euromedecine', 'Nexity Studea Montferrand', 'Nexity Studea Montpellier Millenaire', 'Nexity Studea Paul Valery 2', 'Nexity Studea Zuccarelli'],
  Nantes: ['Nexity Studea Ile Beaulieu', 'Nexity Studea Loquidy', 'Nexity Studea Yleo'],
  Nice: ['Nexity Studea California', 'Nexity Studea Nice Centre', 'Nexity Studea Nice Valrose', 'Nexity Studea Riquier'],
  Palaiseau: ['Nexity Studea Palaiseau Centre'],
  Perpignan: ['Nexity Studea Perpignan'],
  Rennes: ['Nexity Studea Atalante'],
  Roubaix: ['Nexity Studea Roubaix', 'Nexity Studea Roubaix Parc Barbieux'],
  Rouen: ['Nexity Studea Rouen Constantine', 'Nexity Studea Rouen Prefecture'],
  Serris: ['Nexity Studea Val D Europe 1', "Nexity Studea Val D'europe 2"],
  Sevres: ['Nexity Studea Sevres'],
  Strasbourg: ['Nexity Studea Strasbourg Rieth', 'Nexity Studea Strasbourg Rieth 2', 'Nexity Studea Winston 2'],
  Talence: ['Nexity Studea Talence'],
  Toulouse: ['Nexity Studea Toulouse Cartoucherie', 'Nexity Studea Toulouse Compans Caffarelli', 'Nexity Studea Toulouse West', 'Nexity Studea Toulouse Purpan', 'Nexity Studea Toulouse Rangueil'],
  Tours: ['Nexity Studea Gare'],
  Valbonne: ['Nexity Studea Sophia Antipolis'],
  Valenciennes: ['Nexity Studea Valenciennes Mont Houy'],
  Villeurbanne: ['Nexity Studea Charpennes 1', 'Nexity Studea La Doua', 'Nexity Studea Montchat', 'Nexity Studea Villeurbanne Republique 1', 'Nexity Studea Villeurbanne Republique 2']
};

const houseCities = Object.keys(houseResidencesByCity).concat('Paris');
const DASHBOARD_LANGUAGE_STORAGE_KEY = 'adeef.userDashboard.language';

const formatDate = (value, locale = 'fr-FR') => {
  return formatDateInParis(value, { locale, fallback: '-' });
};

const formatDateTime = (value, locale = 'fr-FR') => {
  return formatDateTimeInParis(value, { locale, fallback: '-' });
};

const getTodayDateInputValue = () => {
  return toDateInputValueInParis(new Date());
};

const truncateSnippet = (value, maxLength = 120) => {
  if (value === null || value === undefined) return '';
  const normalized = String(value).replace(/\s+/g, ' ').trim();
  if (!normalized) return '';
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
};

const isInvoicePendingStatus = (status) => {
  const s = String(status || '').toLowerCase();
  if (!s) return false;
  return ['pending', 'unpaid', 'due', 'overdue', 'failed', 'processing', 'open'].some((token) => s.includes(token));
};

const DOC_REQUEST_TYPES = Object.freeze([
  {
    key: 'attestation_de_domicile',
    label: 'Attestation de domicile',
    labelEn: 'Proof of residence',
    requestField: 'requested_attestation_de_domicile',
    fileField: 'attestation_de_domicile_file_url',
  },
  {
    key: 'assurance_habitation',
    label: 'Assurance habitation',
    labelEn: 'Home insurance certificate',
    requestField: 'requested_assurance_habitation',
    fileField: 'assurance_habitation_file_url',
  },
  {
    key: 'quittance_de_loyer',
    label: 'Quittance de loyer',
    labelEn: 'Rent receipt',
    requestField: 'requested_quittance_de_loyer',
    fileField: 'quittance_de_loyer_file_url',
  },
  {
    key: 'attestation_de_loyer_caf',
    label: 'Attestation de loyer CAF',
    labelEn: 'CAF rent certificate',
    requestField: 'requested_attestation_de_loyer_caf',
    fileField: 'attestation_de_loyer_caf_file_url',
  },
]);

const PASSWORD_OTP_COOLDOWN_SECONDS = 30;

const normalizePhoneDigits = (value) => String(value || '').replace(/\D+/g, '');

const formatMonthKey = (value) => {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{4})-(\d{2})(?:-\d{2})?/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (!Number.isFinite(year) || !Number.isFinite(month) || month < 1 || month > 12) return null;
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
};

const monthKeyToLabel = (monthKey, locale = 'fr-FR') => {
  const key = formatMonthKey(monthKey);
  if (!key) return '-';
  const [year, month] = key.split('-').map(Number);
  return formatMonthYearInParis(year, month, locale);
};

const formatCafPeriodLabel = (fromMonth, tillMonth, locale = 'fr-FR') => {
  const fromKey = formatMonthKey(fromMonth);
  const tillKey = formatMonthKey(tillMonth);
  if (!fromKey || !tillKey) return '';
  if (fromKey === tillKey) return monthKeyToLabel(fromKey, locale);
  return `${monthKeyToLabel(fromKey, locale)} -> ${monthKeyToLabel(tillKey, locale)}`;
};

const buildEligibleCafMonths = (moveInDate, now = new Date(), locale = 'fr-FR') => {
  const joined = getDatePartsInParis(moveInDate);
  const current = getDatePartsInParis(now);
  if (!joined || !current) return [];

  let minYear = joined.year;
  let minMonth = joined.month + 1;
  if (minMonth > 12) {
    minMonth = 1;
    minYear += 1;
  }

  let maxYear = current.year;
  let maxMonth = current.month - 1;
  if (maxMonth < 1) {
    maxMonth = 12;
    maxYear -= 1;
  }

  const toToken = (year, month) => (year * 100) + month;
  if (toToken(minYear, minMonth) > toToken(maxYear, maxMonth)) {
    return [];
  }

  const months = [];
  let year = minYear;
  let month = minMonth;

  while (toToken(year, month) <= toToken(maxYear, maxMonth)) {
    const key = `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`;
    months.push({ key, label: monthKeyToLabel(key, locale) });

    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }

  return months;
};

const getDocumentTypeLabel = (docType, language = 'fr') => (
  language === 'en' ? docType.labelEn : docType.label
);

const getDocumentRequestSummary = (request, language = 'fr', locale = 'fr-FR') => {
  const labels = [];
  DOC_REQUEST_TYPES.forEach((docType) => {
    if (Number(request?.[docType.requestField]) !== 1) return;
    const localizedDocLabel = getDocumentTypeLabel(docType, language);
    if (docType.key === 'quittance_de_loyer') {
      const period = formatCafPeriodLabel(request?.caf_period_from_month, request?.caf_period_till_month, locale);
      labels.push(period ? `${localizedDocLabel} (${period})` : localizedDocLabel);
      return;
    }
    labels.push(localizedDocLabel);
  });
  if (!labels.length) return language === 'en' ? 'No document selected' : 'Aucun document sélectionné';
  return labels.join(' • ');
};

const getPaginationItems = (totalPages, currentPage) => {
  const total = Math.max(1, Number(totalPages) || 1);
  const current = Math.min(total, Math.max(1, Number(currentPage) || 1));
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const items = [];
  items.push(1);

  const start = Math.max(2, current - 2);
  const end = Math.min(total - 1, current + 2);

  if (start > 2) items.push('…');
  for (let p = start; p <= end; p += 1) items.push(p);
  if (end < total - 1) items.push('…');

  items.push(total);
  return items;
};

const formatMoney = (amount, currency) => {
  const n = Number(amount);
  if (!Number.isFinite(n)) return '-';
  const cur = String(currency || '').toUpperCase();
  const symbol = cur === 'EUR' ? '€' : cur === 'GBP' ? '£' : `${cur} `;
  return `${symbol}${n.toFixed(2)}`;
};

const buildInvoicePrintHtml = (invoice, options = {}) => {
  if (!invoice) return '';
  const language = options.language === 'en' ? 'en' : 'fr';
  const locale = options.locale || (language === 'en' ? 'en-US' : 'fr-FR');
  const tr = (frText, enText) => (language === 'en' ? enText : frText);
  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const items = Array.isArray(invoice.items) ? invoice.items : [];
  const rows = items
    .map((it) => {
      const label = esc(it.label);
      const amt = esc(formatMoney(it.amount, invoice.currency));
      return `<tr><td>${label}</td><td style="text-align:right">${amt}</td></tr>`;
    })
    .join('');

  const tenantName = esc(`${invoice.first_name || ''} ${invoice.last_name || ''}`.trim() || invoice.email || '');
  const tenantAddress = esc([invoice.residence_address, invoice.city_of_residence].filter(Boolean).join(', '));
  const invoiceNo = esc(invoice.invoice_number);
  const issuedAt = esc(formatDate(invoice.issued_at, locale));
  const paidAt = esc(formatDate(invoice.paid_at, locale));
  const total = esc(formatMoney(invoice.total_amount, invoice.currency));

  return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>${invoiceNo}</title>
    <style>
      * { box-sizing: border-box; }
      body { font-family: Arial, sans-serif; color: #111; margin: 24px; }
      .header { display:flex; justify-content:space-between; gap:16px; }
      .brand { font-weight: 800; font-size: 18px; }
      .muted { color: #555; font-size: 12px; }
      .meta { margin-top: 18px; display:flex; justify-content:space-between; gap:16px; }
      .box { border: 1px solid #ddd; border-radius: 10px; padding: 14px; }
      h1 { font-size: 20px; margin: 0; }
      table { width:100%; border-collapse: collapse; margin-top: 16px; }
      th, td { padding: 10px 8px; border-bottom: 1px solid #eee; }
      th { text-align:left; font-size: 12px; color:#555; }
      .totalRow td { border-top: 2px solid #111; border-bottom: none; font-weight: 800; }
      @media print { body { margin: 0; } }
    </style>
  </head>
  <body>
    <div class="header">
      <div>
        <div class="brand">ADEEF Portal</div>
        <div class="muted">${esc(tr('Facture générée à partir du paiement', 'Invoice generated from payment'))}</div>
      </div>
      <div style="text-align:right">
        <h1>${esc(tr('Facture', 'Invoice'))}</h1>
        <div class="muted">${invoiceNo}</div>
      </div>
    </div>

    <div class="meta">
      <div class="box" style="flex:1">
        <div class="muted">${esc(tr('Facturé à', 'Billed to'))}</div>
        <div style="font-weight:700; margin-top:6px">${tenantName}</div>
        <div class="muted" style="margin-top:4px">${tenantAddress}</div>
      </div>
      <div class="box" style="width: 260px">
        <div class="muted">${esc(tr('Émise le', 'Issued on'))}</div>
        <div style="font-weight:700; margin-top:6px">${issuedAt}</div>
        <div class="muted" style="margin-top:10px">${esc(tr('Payée le', 'Paid on'))}</div>
        <div style="font-weight:700; margin-top:6px">${paidAt}</div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>${esc(tr('Description', 'Description'))}</th>
          <th style="text-align:right">${esc(tr('Montant', 'Amount'))}</th>
        </tr>
      </thead>
      <tbody>
        ${rows}
        <tr class="totalRow">
          <td>${esc(tr('Total', 'Total'))}</td>
          <td style="text-align:right">${total}</td>
        </tr>
      </tbody>
    </table>

    <div class="muted" style="margin-top: 18px">${esc(tr('Référence de paiement', 'Payment reference'))} : ${esc(invoice.gocardless_payment_id || '')}</div>
  </body>
</html>`;
};

const daysInMonth = (year, monthIndex) => new Date(year, monthIndex + 1, 0).getDate();

const addMonthsPreserveDay = (date, monthsToAdd) => {
  const src = new Date(date);
  const day = src.getDate();
  const year = src.getFullYear();
  const month = src.getMonth();

  const targetMonthIndex = month + monthsToAdd;
  const targetYear = year + Math.floor(targetMonthIndex / 12);
  const targetMonth = ((targetMonthIndex % 12) + 12) % 12;

  const maxDay = daysInMonth(targetYear, targetMonth);
  const next = new Date(src);
  next.setFullYear(targetYear);
  next.setMonth(targetMonth, Math.min(day, maxDay));
  return next;
};

const computeNextPaymentDate = (moveInDate) => {
  if (!moveInDate) return null;
  const moveIn = new Date(moveInDate);
  if (Number.isNaN(moveIn.getTime())) return null;

  const today = new Date();
  const todayYMD = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const moveYMD = new Date(moveIn.getFullYear(), moveIn.getMonth(), moveIn.getDate());

  if (moveYMD > todayYMD) {
    return addMonthsPreserveDay(moveYMD, 1);
  }

  const monthsDiff = (todayYMD.getFullYear() - moveYMD.getFullYear()) * 12 + (todayYMD.getMonth() - moveYMD.getMonth());
  const candidate = addMonthsPreserveDay(moveYMD, Math.max(0, monthsDiff));
  if (candidate > todayYMD) return candidate;
  return addMonthsPreserveDay(candidate, 1);
};

const UserDashboard = () => {
  const { user, logout, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [dashboardLanguage, setDashboardLanguage] = useState(() => {
    if (typeof window === 'undefined') return 'fr';
    const stored = window.localStorage.getItem(DASHBOARD_LANGUAGE_STORAGE_KEY);
    return stored === 'en' ? 'en' : 'fr';
  });

  const isEnglishDashboard = dashboardLanguage === 'en';
  const dashboardLocale = isEnglishDashboard ? 'en-US' : 'fr-FR';
  const t = useCallback((frText, enText) => (isEnglishDashboard ? enText : frText), [isEnglishDashboard]);
  const minRequestedHouseDate = useMemo(() => getTodayDateInputValue(), []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(DASHBOARD_LANGUAGE_STORAGE_KEY, dashboardLanguage);
  }, [dashboardLanguage]);

  const nodeRefsByKeyRef = useRef(new Map());
  const tabNodeRef = useMemo(() => {
    const existing = nodeRefsByKeyRef.current.get(activeTab);
    if (existing) return existing;
    const created = React.createRef();
    nodeRefsByKeyRef.current.set(activeTab, created);
    return created;
  }, [activeTab]);

  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const notificationsPageSize = 7;

  const openNotificationModal = useCallback((notification) => {
    setSelectedNotification(notification);
    setShowNotificationModal(true);
  }, []);

  const closeNotificationModal = useCallback(() => {
    setShowNotificationModal(false);
    setTimeout(() => setSelectedNotification(null), 220);
  }, []);

  const fetchNotifications = useCallback(async (signal) => {
    try {
      setNotificationsLoading(true);
      const [notificationsResponse, unreadResponse] = await Promise.all([
        api.notifications.getNotifications({ signal }),
        api.notifications.getUnreadCount({ signal })
      ]);
      setNotifications(notificationsResponse?.data?.data || []);
      setUnreadCount(unreadResponse?.data?.count || 0);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch notifications:', error);
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setNotificationsLoading(false);
    }
  }, []);

  // Invoices
  const [invoices, setInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailsById, setInvoiceDetailsById] = useState({});
  const [invoicesPage, setInvoicesPage] = useState(1);
  const invoicesPageSize = 7;

  const closeInvoiceModal = useCallback(() => {
    setShowInvoiceModal(false);
    setSelectedInvoice(null);
  }, []);

  const fetchInvoices = useCallback(async (signal) => {
    try {
      setInvoicesLoading(true);
      const response = await api.invoices.listMyInvoices({ limit: 500 }, signal ? { signal } : {});
      const payload = response?.data;
      if (payload?.ok) {
        setInvoices(payload.data || []);
      } else {
        setInvoices(payload?.data || []);
      }
    } catch (e) {
      if (e?.name === 'CanceledError' || e?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch invoices:', e);
      setInvoices([]);
    } finally {
      setInvoicesLoading(false);
    }
  }, []);

  const fetchInvoiceDetail = useCallback(async (invoiceId) => {
    const id = Number(invoiceId);
    if (!Number.isFinite(id)) return null;
    try {
      const response = await api.invoices.getMyInvoice(id);
      const payload = response?.data;
      const invoice = payload?.ok ? payload?.data : payload?.data;
      if (invoice) setInvoiceDetailsById((prev) => ({ ...prev, [id]: invoice }));
      return invoice || null;
    } catch (e) {
      console.error('Failed to fetch invoice detail:', e);
      return null;
    }
  }, []);

  const openInvoiceModal = useCallback(async (invoice, { switchToInvoicesTab = false } = {}) => {
    if (!invoice?.id) return;
    const index = invoices.findIndex((row) => row.id === invoice.id);
    if (index >= 0) {
      setInvoicesPage(Math.floor(index / invoicesPageSize) + 1);
    }
    if (switchToInvoicesTab) {
      setActiveTab('invoices');
    }

    const detail = invoiceDetailsById[invoice.id] || (await fetchInvoiceDetail(invoice.id)) || invoice;
    setSelectedInvoice(detail);
    setShowInvoiceModal(true);
  }, [fetchInvoiceDetail, invoiceDetailsById, invoices]);

  const openInvoiceInTab = useCallback(async (invoice) => {
    await openInvoiceModal(invoice, { switchToInvoicesTab: true });
  }, [openInvoiceModal]);

  const markNotificationAsReadOptimistic = useCallback((notificationId) => {
    const current = notifications.find((n) => n.id === notificationId);
    const wasUnread = current ? !current.is_read : false;

    if (wasUnread) {
      setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, is_read: 1 } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }

    api.notifications.markAsRead(notificationId).catch((error) => {
      console.error('Failed to mark notification as read:', error);
      fetchNotifications();
    });
  }, [fetchNotifications, notifications]);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await api.notifications.deleteNotification(notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (selectedNotification?.id === notificationId) {
        setSelectedNotification(null);
        setShowNotificationModal(false);
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    }
  }, [selectedNotification]);

  const deleteSeenNotifications = useCallback(async () => {
    try {
      await api.notifications.clearSeenNotifications();
      setNotifications((prev) => prev.filter((n) => !n.is_read));
      if (selectedNotification?.is_read) {
        setSelectedNotification(null);
        setShowNotificationModal(false);
      }
    } catch (error) {
      console.error('Failed to delete seen notifications:', error);
    }
  }, [selectedNotification]);

  // Document requests
  const [documentRequests, setDocumentRequests] = useState([]);
  const [documentRequestsLoading, setDocumentRequestsLoading] = useState(false);
  const [documentRequestsSubmitting, setDocumentRequestsSubmitting] = useState(false);
  const [documentRequestsPage, setDocumentRequestsPage] = useState(1);
  const documentRequestsPageSize = 7;
  const [showDocumentRequestModal, setShowDocumentRequestModal] = useState(false);
  const [showDocumentRequestComposer, setShowDocumentRequestComposer] = useState(false);
  const [selectedDocumentRequest, setSelectedDocumentRequest] = useState(null);
  const [documentRequestForm, setDocumentRequestForm] = useState({
    selected_type: '',
    caf_period_from_month: '',
    caf_period_till_month: '',
  });
  const [documentRequestNotice, setDocumentRequestNotice] = useState('');

  // House management
  const [houseRequests, setHouseRequests] = useState([]);
  const [houseRequestsLoading, setHouseRequestsLoading] = useState(false);
  const [houseRequestsSubmitting, setHouseRequestsSubmitting] = useState(false);
  const [houseRequestsPage, setHouseRequestsPage] = useState(1);
  const houseRequestsPageSize = 7;
  const [houseRequestNotice, setHouseRequestNotice] = useState('');

  const [showChangeResidenceModal, setShowChangeResidenceModal] = useState(false);
  const [showLeaveResidenceModal, setShowLeaveResidenceModal] = useState(false);

  const [changeResidenceForm, setChangeResidenceForm] = useState({
    requested_move_date: '',
    desired_city: '',
    desired_region: '',
    desired_residences: [],
  });

  const [leaveResidenceForm, setLeaveResidenceForm] = useState({
    requested_leave_date: '',
    confirm_termination: false,
  });

  // Settings: password flow
  const [settingsExpandedPanel, setSettingsExpandedPanel] = useState('');
  const [passwordOtp, setPasswordOtp] = useState('');
  const [passwordResetToken, setPasswordResetToken] = useState('');
  const [passwordNewValue, setPasswordNewValue] = useState('');
  const [passwordConfirmValue, setPasswordConfirmValue] = useState('');
  const [passwordCooldown, setPasswordCooldown] = useState(0);
  const [passwordBusyState, setPasswordBusyState] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');

  // Settings: phone flow
  const [phoneDraft, setPhoneDraft] = useState('');
  const [phoneConfirmStage, setPhoneConfirmStage] = useState(false);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [phoneMessage, setPhoneMessage] = useState('');
  const [phoneError, setPhoneError] = useState('');

  const resetDocumentRequestComposer = useCallback(() => {
    setDocumentRequestForm({
      selected_type: '',
      caf_period_from_month: '',
      caf_period_till_month: '',
    });
    setShowDocumentRequestComposer(false);
  }, []);

  const resetChangeResidenceForm = useCallback(() => {
    setChangeResidenceForm({
      requested_move_date: '',
      desired_city: '',
      desired_region: '',
      desired_residences: [],
    });
  }, []);

  const resetLeaveResidenceForm = useCallback(() => {
    setLeaveResidenceForm({
      requested_leave_date: '',
      confirm_termination: false,
    });
  }, []);

  const closeChangeResidenceModal = useCallback(() => {
    setShowChangeResidenceModal(false);
    resetChangeResidenceForm();
  }, [resetChangeResidenceForm]);

  const closeLeaveResidenceModal = useCallback(() => {
    setShowLeaveResidenceModal(false);
    resetLeaveResidenceForm();
  }, [resetLeaveResidenceForm]);

  const fetchHouseManagementRequests = useCallback(async (signal) => {
    try {
      setHouseRequestsLoading(true);
      const response = await api.houseManagement.listMine({ limit: 200 }, signal ? { signal } : {});
      setHouseRequests(response?.data?.data || []);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch house-management requests:', error);
      setHouseRequests([]);
    } finally {
      setHouseRequestsLoading(false);
    }
  }, []);

  const eligibleCafMonths = useMemo(
    () => buildEligibleCafMonths(user?.move_in_date, new Date(), dashboardLocale),
    [dashboardLocale, user?.move_in_date]
  );

  const cafTillMonthOptions = useMemo(() => {
    const from = formatMonthKey(documentRequestForm.caf_period_from_month);
    if (!from) return eligibleCafMonths;
    return eligibleCafMonths.filter((m) => m.key >= from);
  }, [eligibleCafMonths, documentRequestForm.caf_period_from_month]);

  const closeDocumentRequestModal = useCallback(() => {
    setShowDocumentRequestModal(false);
    setSelectedDocumentRequest(null);
  }, []);

  const openDocumentRequestModal = useCallback((row) => {
    setSelectedDocumentRequest(row);
    setShowDocumentRequestModal(true);
  }, []);

  const fetchMyDocumentRequests = useCallback(async (signal) => {
    try {
      setDocumentRequestsLoading(true);
      const resp = await api.documentRequests.listMine({ limit: 200 }, signal ? { signal } : {});
      setDocumentRequests(resp?.data?.data || []);
    } catch (error) {
      if (error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch document requests:', error);
      setDocumentRequests([]);
    } finally {
      setDocumentRequestsLoading(false);
    }
  }, []);

  const submitDocumentRequest = useCallback(async () => {
    const selectedType = String(documentRequestForm.selected_type || '').trim();
    if (!selectedType) {
      alert(isEnglishDashboard ? 'Please select a document.' : 'Veuillez sélectionner un document.');
      return;
    }

    const payload = {
      requested_attestation_de_domicile: selectedType === 'attestation_de_domicile',
      requested_assurance_habitation: selectedType === 'assurance_habitation',
      requested_quittance_de_loyer: selectedType === 'quittance_de_loyer',
      requested_attestation_de_loyer_caf: selectedType === 'attestation_de_loyer_caf',
      requested_document_type: selectedType,
      selected_document_type: selectedType,
    };

    if (selectedType === 'quittance_de_loyer') {
      if (eligibleCafMonths.length === 0) {
        alert(isEnglishDashboard ? 'No rent-receipt period is currently available.' : 'Aucune période de quittance disponible pour le moment.');
        return;
      }

      const fromMonth = formatMonthKey(documentRequestForm.caf_period_from_month);
      const tillMonth = formatMonthKey(documentRequestForm.caf_period_till_month);
      if (!fromMonth || !tillMonth) {
        alert(isEnglishDashboard ? 'Please select a rent-receipt period (from and to).' : 'Veuillez sélectionner une période de quittance (de et à).');
        return;
      }
      if (fromMonth > tillMonth) {
        alert(
          isEnglishDashboard
            ? 'The selected period is invalid: the start date must be earlier than or equal to the end date.'
            : 'La période sélectionnée est invalide : la date de début doit être antérieure ou égale à la date de fin.'
        );
        return;
      }

      payload.caf_period_from_month = fromMonth;
      payload.caf_period_till_month = tillMonth;
    }

    try {
      setDocumentRequestsSubmitting(true);
      setDocumentRequestNotice('');
      await api.documentRequests.create(payload);
      setDocumentRequestNotice(
        isEnglishDashboard
          ? 'Request sent. You can submit a new request after 10 days.'
          : 'Demande envoyée. Vous pourrez refaire une demande après 10 jours.'
      );
      resetDocumentRequestComposer();
      setDocumentRequestsPage(1);
      await fetchMyDocumentRequests();
    } catch (error) {
      console.error('Failed to create document request:', error);
      alert(error?.response?.data?.error || (isEnglishDashboard ? 'Unable to create request.' : 'Impossible de créer la demande'));
    } finally {
      setDocumentRequestsSubmitting(false);
    }
  }, [documentRequestForm, eligibleCafMonths.length, fetchMyDocumentRequests, isEnglishDashboard, resetDocumentRequestComposer]);

  const availableChangeResidences = useMemo(() => {
    if (!changeResidenceForm.desired_city) return [];
    if (changeResidenceForm.desired_city === 'Paris') {
      return houseParisResidencesBySubRegion[changeResidenceForm.desired_region] || [];
    }
    return houseResidencesByCity[changeResidenceForm.desired_city] || [];
  }, [changeResidenceForm.desired_city, changeResidenceForm.desired_region]);

  const selectedDesiredResidence = useMemo(() => {
    if (!Array.isArray(changeResidenceForm.desired_residences)) return '';
    return String(changeResidenceForm.desired_residences[0] || '');
  }, [changeResidenceForm.desired_residences]);

  const selectDesiredResidence = useCallback((residenceName) => {
    setChangeResidenceForm((prev) => {
      return {
        ...prev,
        desired_residences: residenceName ? [residenceName] : [],
      };
    });
  }, []);

  const submitChangeResidenceRequest = useCallback(async () => {
    const requestedMoveDate = String(changeResidenceForm.requested_move_date || '').trim();
    const desiredCity = String(changeResidenceForm.desired_city || '').trim();
    const desiredRegion = String(changeResidenceForm.desired_region || '').trim();
    const desiredResidences = Array.isArray(changeResidenceForm.desired_residences)
      ? changeResidenceForm.desired_residences
      : [];

    if (!requestedMoveDate) {
      alert(isEnglishDashboard ? 'Please select your preferred move date.' : 'Veuillez selectionner votre date souhaitee de demenagement.');
      return;
    }
    if (requestedMoveDate < minRequestedHouseDate) {
      alert(isEnglishDashboard ? 'The preferred move date cannot be in the past.' : 'La date souhaitee de demenagement ne peut pas etre dans le passe.');
      return;
    }
    if (!desiredCity) {
      alert(isEnglishDashboard ? 'Please select your preferred city.' : 'Veuillez selectionner la ville souhaitee.');
      return;
    }
    if (desiredCity === 'Paris' && !desiredRegion) {
      alert(isEnglishDashboard ? 'Please select a Paris sub-region.' : 'Veuillez selectionner une sous-region pour Paris.');
      return;
    }
    if (desiredResidences.length === 0) {
      alert(isEnglishDashboard ? 'Please select at least one residence.' : 'Veuillez selectionner au moins une residence.');
      return;
    }
    if (desiredResidences.length !== 1) {
      alert(isEnglishDashboard ? 'Please select exactly one residence.' : 'Veuillez selectionner exactement une residence.');
      return;
    }

    try {
      setHouseRequestsSubmitting(true);
      setHouseRequestNotice('');
      await api.houseManagement.createChangeResidenceRequest({
        requested_move_date: requestedMoveDate,
        desired_city: desiredCity,
        desired_region: desiredRegion || null,
        desired_residences: desiredResidences,
      });
      setHouseRequestNotice(
        isEnglishDashboard
          ? 'Your residence-change request was sent to the administrators.'
          : 'Votre demande de changement de residence a ete envoyee aux administrateurs.'
      );
      closeChangeResidenceModal();
      setHouseRequestsPage(1);
      await fetchHouseManagementRequests();
    } catch (error) {
      console.error('Failed to create change-residence request:', error);
      alert(error?.response?.data?.error || (isEnglishDashboard ? 'Unable to submit request.' : 'Impossible de soumettre la demande.'));
    } finally {
      setHouseRequestsSubmitting(false);
    }
  }, [changeResidenceForm, closeChangeResidenceModal, fetchHouseManagementRequests, isEnglishDashboard, minRequestedHouseDate]);

  const submitLeaveResidenceRequest = useCallback(async () => {
    const requestedLeaveDate = String(leaveResidenceForm.requested_leave_date || '').trim();
    const confirmTermination = Boolean(leaveResidenceForm.confirm_termination);

    if (!requestedLeaveDate) {
      alert(isEnglishDashboard ? 'Please select your preferred leave date.' : 'Veuillez selectionner votre date souhaitee de depart.');
      return;
    }
    if (requestedLeaveDate < minRequestedHouseDate) {
      alert(isEnglishDashboard ? 'The preferred leave date cannot be in the past.' : 'La date souhaitee de depart ne peut pas etre dans le passe.');
      return;
    }
    if (!confirmTermination) {
      alert(isEnglishDashboard ? 'Please confirm the end-of-contract request.' : 'Veuillez confirmer la demande de fin de contrat.');
      return;
    }

    try {
      setHouseRequestsSubmitting(true);
      setHouseRequestNotice('');
      await api.houseManagement.createLeaveResidenceRequest({
        requested_leave_date: requestedLeaveDate,
        confirm_termination: true,
      });
      setHouseRequestNotice(
        isEnglishDashboard
          ? 'Your end-of-contract request was sent to the administrators.'
          : 'Votre demande de fin de contrat a ete envoyee aux administrateurs.'
      );
      closeLeaveResidenceModal();
      setHouseRequestsPage(1);
      await fetchHouseManagementRequests();
    } catch (error) {
      console.error('Failed to create leave-residence request:', error);
      alert(error?.response?.data?.error || (isEnglishDashboard ? 'Unable to submit request.' : 'Impossible de soumettre la demande.'));
    } finally {
      setHouseRequestsSubmitting(false);
    }
  }, [closeLeaveResidenceModal, fetchHouseManagementRequests, isEnglishDashboard, leaveResidenceForm, minRequestedHouseDate]);

  useEffect(() => {
    const controller = new AbortController();
    fetchNotifications(controller.signal);
    const interval = setInterval(() => fetchNotifications(), 30_000);
    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [fetchNotifications]);

  // Prime overview snippets so invoice/document summaries can appear without opening tabs first.
  useEffect(() => {
    const controller = new AbortController();
    fetchInvoices(controller.signal);
    fetchMyDocumentRequests(controller.signal);
    return () => controller.abort();
  }, [fetchInvoices, fetchMyDocumentRequests]);

  useEffect(() => {
    if (activeTab !== 'invoices') return;
    const controller = new AbortController();
    fetchInvoices(controller.signal);
    return () => controller.abort();
  }, [activeTab, fetchInvoices]);

  useEffect(() => {
    if (activeTab !== 'document-requests') return;
    const controller = new AbortController();
    fetchMyDocumentRequests(controller.signal);
    setDocumentRequestsPage(1);
    resetDocumentRequestComposer();
    return () => controller.abort();
  }, [activeTab, fetchMyDocumentRequests, resetDocumentRequestComposer]);

  useEffect(() => {
    if (activeTab !== 'house-management') return;
    const controller = new AbortController();
    fetchHouseManagementRequests(controller.signal);
    setHouseRequestsPage(1);
    setHouseRequestNotice('');
    closeChangeResidenceModal();
    closeLeaveResidenceModal();
    return () => controller.abort();
  }, [activeTab, closeChangeResidenceModal, closeLeaveResidenceModal, fetchHouseManagementRequests]);

  const invoicesTotalPages = useMemo(() => Math.max(1, Math.ceil((invoices.length || 0) / invoicesPageSize)), [invoices.length]);
  const invoicesPageClamped = Math.min(invoicesTotalPages, Math.max(1, invoicesPage));
  useEffect(() => {
    if (invoicesPage !== invoicesPageClamped) setInvoicesPage(invoicesPageClamped);
  }, [invoicesPage, invoicesPageClamped]);

  const documentRequestsTotalPages = useMemo(
    () => Math.max(1, Math.ceil((documentRequests.length || 0) / documentRequestsPageSize)),
    [documentRequests.length]
  );
  const documentRequestsPageClamped = Math.min(documentRequestsTotalPages, Math.max(1, documentRequestsPage));
  useEffect(() => {
    if (documentRequestsPage !== documentRequestsPageClamped) setDocumentRequestsPage(documentRequestsPageClamped);
  }, [documentRequestsPage, documentRequestsPageClamped]);

  const pagedInvoices = useMemo(() => {
    const start = (invoicesPageClamped - 1) * invoicesPageSize;
    return invoices.slice(start, start + invoicesPageSize);
  }, [invoices, invoicesPageClamped]);

  const pagedDocumentRequests = useMemo(() => {
    const start = (documentRequestsPageClamped - 1) * documentRequestsPageSize;
    return documentRequests.slice(start, start + documentRequestsPageSize);
  }, [documentRequests, documentRequestsPageClamped]);

  const houseRequestsTotalPages = useMemo(
    () => Math.max(1, Math.ceil((houseRequests.length || 0) / houseRequestsPageSize)),
    [houseRequests.length]
  );
  const houseRequestsPageClamped = Math.min(houseRequestsTotalPages, Math.max(1, houseRequestsPage));
  useEffect(() => {
    if (houseRequestsPage !== houseRequestsPageClamped) setHouseRequestsPage(houseRequestsPageClamped);
  }, [houseRequestsPage, houseRequestsPageClamped]);

  const pagedHouseRequests = useMemo(() => {
    const start = (houseRequestsPageClamped - 1) * houseRequestsPageSize;
    return houseRequests.slice(start, start + houseRequestsPageSize);
  }, [houseRequests, houseRequestsPageClamped]);

  const notificationsTotalPages = useMemo(
    () => Math.max(1, Math.ceil((notifications.length || 0) / notificationsPageSize)),
    [notifications.length]
  );
  const notificationsPageClamped = Math.min(notificationsTotalPages, Math.max(1, notificationsPage));
  useEffect(() => {
    if (notificationsPage !== notificationsPageClamped) setNotificationsPage(notificationsPageClamped);
  }, [notificationsPage, notificationsPageClamped]);

  const pagedNotifications = useMemo(() => {
    const start = (notificationsPageClamped - 1) * notificationsPageSize;
    return notifications.slice(start, start + notificationsPageSize);
  }, [notifications, notificationsPageClamped]);

  const notificationsSorted = useMemo(() => {
    if (!notifications.length) return [];
    return [...notifications].sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [notifications]);

  const unreadNotificationsSorted = useMemo(
    () => notificationsSorted.filter((n) => !n?.is_read),
    [notificationsSorted]
  );

  const overviewNotifications = useMemo(() => {
    if (unreadNotificationsSorted.length > 0) return unreadNotificationsSorted.slice(0, 3);
    return notificationsSorted.slice(0, 2);
  }, [notificationsSorted, unreadNotificationsSorted]);

  const invoicesSorted = useMemo(() => {
    if (!invoices.length) return [];
    return [...invoices].sort((a, b) => {
      const aTime = new Date(a?.issued_at || a?.created_at || 0).getTime();
      const bTime = new Date(b?.issued_at || b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [invoices]);

  const pendingInvoices = useMemo(
    () => invoicesSorted.filter((invoice) => isInvoicePendingStatus(invoice?.status)),
    [invoicesSorted]
  );

  const overviewInvoices = useMemo(() => {
    if (pendingInvoices.length > 1) return pendingInvoices.slice(0, 5);
    return invoicesSorted.slice(0, 1);
  }, [invoicesSorted, pendingInvoices]);

  const documentRequestsSorted = useMemo(() => {
    if (!documentRequests.length) return [];
    return [...documentRequests].sort((a, b) => {
      const aTime = new Date(a?.requested_at || a?.created_at || 0).getTime();
      const bTime = new Date(b?.requested_at || b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [documentRequests]);

  const pendingDocumentRequests = useMemo(
    () => documentRequestsSorted.filter((r) => String(r?.status || '').toLowerCase() !== 'completed'),
    [documentRequestsSorted]
  );

  const pendingDocumentRequestsCount = useMemo(
    () => pendingDocumentRequests.length,
    [pendingDocumentRequests]
  );

  const overviewDocumentRequests = useMemo(() => {
    if (pendingDocumentRequests.length > 1) return pendingDocumentRequests.slice(0, 5);
    return documentRequestsSorted.slice(0, 1);
  }, [documentRequestsSorted, pendingDocumentRequests]);

  const displayName = useMemo(() => {
    const full = `${user?.first_name || ''} ${user?.last_name || ''}`.trim();
    return full || user?.email || t('Utilisateur', 'User');
  }, [t, user]);

  const apartmentLabel = useMemo(() => {
    const res = String(user?.residence_name || '').trim();
    const studio = String(user?.studio_number || '').trim();
    const city = String(user?.city_of_residence || '').trim();
    const parts = [];
    if (res) parts.push(res);
    if (studio) parts.push(`Studio ${studio}`);
    if (city) parts.push(city);
    return parts.length ? parts.join(' • ') : '-';
  }, [user]);

  const nextPaymentDate = useMemo(() => {
    const next = computeNextPaymentDate(user?.move_in_date);
    return next ? formatDateInParis(next, { locale: dashboardLocale, fallback: '-' }) : '-';
  }, [dashboardLocale, user?.move_in_date]);

  const monthlyPaymentAmount = useMemo(() => {
    if (user?.payment_amount === null || user?.payment_amount === undefined || user?.payment_amount === '') {
      return '-';
    }
    return `EUR ${Number(user.payment_amount).toFixed(2)}`;
  }, [user?.payment_amount]);

  useEffect(() => {
    if (activeTab !== 'notifications') return;
    setNotificationsPage(1);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'house-management') return;
    setShowChangeResidenceModal(false);
    setShowLeaveResidenceModal(false);
  }, [activeTab]);

  useEffect(() => {
    if (passwordCooldown <= 0) return;
    const timer = window.setTimeout(() => {
      setPasswordCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [passwordCooldown]);

  const openPasswordSettingsPanel = useCallback(() => {
    setSettingsExpandedPanel('password');
    setPasswordOtp('');
    setPasswordResetToken('');
    setPasswordNewValue('');
    setPasswordConfirmValue('');
    setPasswordMessage('');
    setPasswordError('');
    setPhoneConfirmStage(false);
    setPhoneMessage('');
    setPhoneError('');
  }, []);

  const cancelPasswordSettingsPanel = useCallback(() => {
    setSettingsExpandedPanel('');
    setPasswordOtp('');
    setPasswordResetToken('');
    setPasswordNewValue('');
    setPasswordConfirmValue('');
    setPasswordMessage('');
    setPasswordError('');
    setPasswordBusyState('');
  }, []);

  const sendPasswordResetOtp = useCallback(async () => {
    const email = String(user?.email || '').trim();
    if (!email) {
      setPasswordError(
        isEnglishDashboard
          ? 'Email address was not found to send the code.'
          : 'Adresse email introuvable pour envoyer le code.'
      );
      return;
    }
    if (passwordCooldown > 0) return;

    try {
      setPasswordBusyState('sending');
      setPasswordMessage('');
      setPasswordError('');
      await api.auth.requestPasswordReset(email);
      setPasswordMessage(
        isEnglishDashboard
          ? 'Code sent. Check your email then enter the code below.'
          : 'Code envoyé. Vérifiez votre email puis saisissez le code ci-dessous.'
      );
      setPasswordCooldown(PASSWORD_OTP_COOLDOWN_SECONDS);
      setPasswordResetToken('');
      setPasswordNewValue('');
      setPasswordConfirmValue('');
    } catch (error) {
      setPasswordError(
        error?.response?.data?.error
          || (isEnglishDashboard ? 'Unable to send the code right now.' : 'Impossible d\'envoyer le code pour le moment.')
      );
    } finally {
      setPasswordBusyState('');
    }
  }, [isEnglishDashboard, passwordCooldown, user?.email]);

  const verifyPasswordResetCode = useCallback(async () => {
    const email = String(user?.email || '').trim();
    const code = String(passwordOtp || '').trim();
    if (!email) {
      setPasswordError(isEnglishDashboard ? 'Email address was not found.' : 'Adresse email introuvable.');
      return;
    }
    if (!/^\d{6}$/.test(code)) {
      setPasswordError(isEnglishDashboard ? 'The code must contain 6 digits.' : 'Le code doit contenir 6 chiffres.');
      return;
    }

    try {
      setPasswordBusyState('verifying');
      setPasswordMessage('');
      setPasswordError('');
      const response = await api.auth.verifyPasswordResetOtp(email, code);
      const token = String(response?.data?.resetToken || '').trim();
      if (!token) {
        setPasswordError(isEnglishDashboard ? 'Invalid code. Please try again.' : 'Code invalide. Veuillez réessayer.');
        return;
      }
      setPasswordResetToken(token);
      setPasswordMessage(
        isEnglishDashboard
          ? 'Code confirmed. You can now enter your new password.'
          : 'Code confirmé. Saisissez maintenant votre nouveau mot de passe.'
      );
    } catch (error) {
      setPasswordError(error?.response?.data?.error || (isEnglishDashboard ? 'Invalid code. Please try again.' : 'Code invalide. Veuillez réessayer.'));
    } finally {
      setPasswordBusyState('');
    }
  }, [isEnglishDashboard, passwordOtp, user?.email]);

  const submitNewPasswordFromSettings = useCallback(async () => {
    const email = String(user?.email || '').trim();
    const nextPassword = String(passwordNewValue || '');
    const confirmPassword = String(passwordConfirmValue || '');

    if (!email) {
      setPasswordError(isEnglishDashboard ? 'Email address was not found.' : 'Adresse email introuvable.');
      return;
    }
    if (!passwordResetToken) {
      setPasswordError(isEnglishDashboard ? 'Please verify the code sent by email first.' : 'Veuillez d\'abord vérifier le code envoyé par email.');
      return;
    }
    if (nextPassword.length < 8) {
      setPasswordError(isEnglishDashboard ? 'Password must contain at least 8 characters.' : 'Le mot de passe doit contenir au moins 8 caractères.');
      return;
    }
    if (nextPassword !== confirmPassword) {
      setPasswordError(isEnglishDashboard ? 'Passwords do not match.' : 'Les mots de passe ne correspondent pas.');
      return;
    }

    try {
      setPasswordBusyState('saving');
      setPasswordMessage('');
      setPasswordError('');
      await api.auth.resetPassword(email, passwordResetToken, nextPassword);
      setPasswordMessage(isEnglishDashboard ? 'Password updated successfully.' : 'Mot de passe mis à jour avec succès.');
      setPasswordOtp('');
      setPasswordResetToken('');
      setPasswordNewValue('');
      setPasswordConfirmValue('');
    } catch (error) {
      setPasswordError(error?.response?.data?.error || (isEnglishDashboard ? 'Unable to update password.' : 'Impossible de mettre à jour le mot de passe.'));
    } finally {
      setPasswordBusyState('');
    }
  }, [isEnglishDashboard, passwordConfirmValue, passwordNewValue, passwordResetToken, user?.email]);

  const openPhoneSettingsPanel = useCallback(() => {
    setSettingsExpandedPanel('phone');
    setPhoneDraft(normalizePhoneDigits(user?.phone_number || ''));
    setPhoneConfirmStage(false);
    setPhoneMessage('');
    setPhoneError('');
    setPasswordMessage('');
    setPasswordError('');
  }, [user?.phone_number]);

  const cancelPhoneSettingsPanel = useCallback(() => {
    setSettingsExpandedPanel('');
    setPhoneDraft(normalizePhoneDigits(user?.phone_number || ''));
    setPhoneConfirmStage(false);
    setPhoneMessage('');
    setPhoneError('');
    setPhoneBusy(false);
  }, [user?.phone_number]);

  const continuePhoneConfirmation = useCallback(() => {
    const normalized = normalizePhoneDigits(phoneDraft);
    if (!/^\d{6,20}$/.test(normalized)) {
      setPhoneError(isEnglishDashboard ? 'The phone number must contain digits only (6 to 20).' : 'Le numéro doit contenir uniquement des chiffres (6 à 20).');
      setPhoneConfirmStage(false);
      return;
    }

    setPhoneDraft(normalized);
    setPhoneError('');
    setPhoneMessage('');
    setPhoneConfirmStage(true);
  }, [isEnglishDashboard, phoneDraft]);

  const confirmPhoneUpdate = useCallback(async () => {
    const normalized = normalizePhoneDigits(phoneDraft);
    if (!/^\d{6,20}$/.test(normalized)) {
      setPhoneError(isEnglishDashboard ? 'The phone number must contain digits only (6 to 20).' : 'Le numéro doit contenir uniquement des chiffres (6 à 20).');
      setPhoneConfirmStage(false);
      return;
    }

    try {
      setPhoneBusy(true);
      setPhoneError('');
      setPhoneMessage('');
      await api.users.updateMyPhone(normalized);
      await refreshUser();
      setPhoneMessage(isEnglishDashboard ? 'Phone number updated.' : 'Numéro de téléphone mis à jour.');
      setPhoneConfirmStage(false);
    } catch (error) {
      setPhoneError(error?.response?.data?.error || (isEnglishDashboard ? 'Unable to update phone number.' : 'Impossible de mettre à jour le numéro.'));
    } finally {
      setPhoneBusy(false);
    }
  }, [isEnglishDashboard, phoneDraft, refreshUser]);

  const settingsReadOnlyItems = useMemo(() => ([
    { label: t('Prénom', 'First name'), value: user?.first_name || '-' },
    { label: t('Nom', 'Last name'), value: user?.last_name || '-' },
    { label: t('Email', 'Email'), value: user?.email || '-' },
    { label: t('Téléphone', 'Phone'), value: normalizePhoneDigits(user?.phone_number || '') || '-' },
    { label: t('Résidence', 'Residence'), value: user?.residence_name || '-' },
    { label: t('Adresse', 'Address'), value: user?.residence_address || '-' },
    { label: t('Ville', 'City'), value: user?.city_of_residence || '-' },
    { label: t('Studio', 'Studio'), value: user?.studio_number || '-' },
    { label: t('Contrat', 'Contract'), value: user?.contract_type || '-' },
    { label: t('Montant du paiement', 'Payment amount'), value: monthlyPaymentAmount },
    { label: t('Date d\'entrée', 'Move-in date'), value: formatDate(user?.move_in_date, dashboardLocale) },
    { label: t('Date de sortie', 'Move-out date'), value: formatDate(user?.move_out_date, dashboardLocale) },
  ]), [dashboardLocale, monthlyPaymentAmount, t, user]);

  const isPasswordPanelOpen = settingsExpandedPanel === 'password';
  const isPhonePanelOpen = settingsExpandedPanel === 'phone';

  return (
    <div className="admin-dashboard admin-shell user-dashboard">
      <div
        className={`admin-mobile-backdrop ${mobileMenuOpen ? 'is-visible' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
      />
      <button
        type="button"
        className="admin-mobile-menu-btn"
        onClick={() => setMobileMenuOpen((prev) => !prev)}
        aria-label={mobileMenuOpen ? t('Fermer le menu', 'Close menu') : t('Ouvrir le menu', 'Open menu')}
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          {mobileMenuOpen ? (
            <>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </>
          ) : (
            <>
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </>
          )}
        </svg>
      </button>
      <aside className={`admin-sidebar ${mobileMenuOpen ? 'is-mobile-open' : ''}`}>
        <div className="admin-brand">
          <div className="admin-brand-title">ADEEF Portal</div>
          <div className="admin-brand-subtitle">{t('Locataire', 'Tenant')}</div>
        </div>

        <div className="admin-side-nav">
          <button
            type="button"
            className={`admin-side-item ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              if (activeTab !== 'overview') setActiveTab('overview');
            }}
          >
            {t('Aperçu', 'Overview')}
          </button>

          <button
            type="button"
            className={`admin-side-item ${activeTab === 'invoices' ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              if (activeTab !== 'invoices') setActiveTab('invoices');
            }}
          >
            {t('Factures', 'Invoices')}
          </button>

          <button
            type="button"
            className={`admin-side-item ${activeTab === 'notifications' ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              if (activeTab !== 'notifications') setActiveTab('notifications');
            }}
          >
            <span className="user-side-item">
              {t('Notifications', 'Notifications')}
              {unreadCount > 0 && <span className="user-nav-dot" aria-label={t('Notifications non lues', 'Unread notifications')} />}
            </span>
          </button>

          <button
            type="button"
            className={`admin-side-item ${activeTab === 'document-requests' ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              if (activeTab !== 'document-requests') {
                setDocumentRequestNotice('');
                setActiveTab('document-requests');
              }
            }}
          >
            {t('Demande de documents', 'Document requests')}
          </button>

          <button
            type="button"
            className={`admin-side-item ${activeTab === 'house-management' ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              if (activeTab !== 'house-management') {
                setHouseRequestNotice('');
                setActiveTab('house-management');
              }
            }}
          >
            {t('Gestion du logement', 'Housing management')}
          </button>
        </div>

        <div className="admin-side-footer">
          <button
            type="button"
            className={`admin-side-item ${activeTab === 'settings' ? 'active' : ''}`}
            onClick={() => {
              setMobileMenuOpen(false);
              if (activeTab !== 'settings') setActiveTab('settings');
            }}
          >
            {t('Paramètres', 'Settings')}
          </button>

          <button type="button" className="admin-side-item" onClick={logout}>
            {t('Se déconnecter', 'Log out')}
          </button>
        </div>
      </aside>

      <div className="admin-main">
        <main className="admin-window">
          <div className="admin-tab-viewport">
            <TransitionGroup className="admin-tab-stage">
              <CSSTransition
                key={activeTab}
                nodeRef={tabNodeRef}
                classNames="admin-fade"
                timeout={220}
              >
                <div ref={tabNodeRef} className="admin-tab-page">
                  {activeTab === 'overview' && (
                    <div className="admin-page">
                      <div className="admin-page-head">
                        <h1 className="admin-page-title">{t('Aperçu', 'Overview')}</h1>
                        <p className="admin-page-desc user-overview-welcome">
                          {t('Bon retour,', 'Welcome back,')}
                          <span className="user-overview-welcome-name">{displayName}</span>
                        </p>
                      </div>

                      <div className="user-overview-layout">
                        <div className="user-overview-stack">
                          <div className="user-overview-stack-item">
                            <span className="user-overview-label">{t('Prochaine date de paiement', 'Next payment date')}</span>
                            <span className="user-overview-value">{nextPaymentDate}</span>
                          </div>

                          <div className="user-overview-stack-item user-overview-stack-item--amount">
                            <span className="user-overview-label">{t('Montant du paiement', 'Payment amount')}</span>
                            <span className="user-overview-value user-overview-value--large">{monthlyPaymentAmount}</span>
                          </div>

                          <div className="user-overview-stack-item">
                            <span className="user-overview-label">{t('Localisation', 'Location')}</span>
                            <span className="user-overview-value">{apartmentLabel}</span>
                          </div>

                          <div className="user-overview-stack-item">
                            <span className="user-overview-label">{t('Date d\'entrée', 'Move-in date')}</span>
                            <span className="user-overview-value">{formatDate(user?.move_in_date, dashboardLocale)}</span>
                          </div>
                        </div>

                        <div className="user-overview-side">
                          <div className="user-overview-side-card">
                            <div className="user-overview-side-head">
                              <h2 className="user-overview-side-title">{t('Aperçu des notifications', 'Notifications overview')}</h2>
                              <button
                                type="button"
                                className="admin-page-btn"
                                onClick={() => setActiveTab('notifications')}
                              >
                                {t('Ouvrir', 'Open')}
                              </button>
                            </div>
                            <div className="user-overview-side-main">
                              {isEnglishDashboard ? `${unreadCount} unread` : `${unreadCount} non lues`}
                            </div>
                            <div className="user-overview-side-sub">
                              {notificationsLoading
                                ? t('Chargement…', 'Loading...')
                                : isEnglishDashboard
                                  ? `${overviewNotifications.length} item${overviewNotifications.length === 1 ? '' : 's'} shown`
                                  : `${overviewNotifications.length} élément${overviewNotifications.length === 1 ? '' : 's'} affiché${overviewNotifications.length === 1 ? '' : 's'}`}
                            </div>

                            <div className="user-overview-mini-list">
                              {notificationsLoading ? (
                                <div className="user-overview-mini-empty">{t('Chargement des notifications…', 'Loading notifications...')}</div>
                              ) : overviewNotifications.length === 0 ? (
                                <div className="user-overview-mini-empty">{t('Aucune notification pour le moment.', 'No notifications at the moment.')}</div>
                              ) : (
                                overviewNotifications.map((notification) => {
                                  const unread = !notification?.is_read;
                                  return (
                                    <article key={notification.id} className="user-overview-mini-item">
                                      <div className="user-overview-mini-top">
                                        <div className="user-overview-mini-meta">
                                          <span
                                            className={`user-overview-mini-dot ${unread ? 'is-unread' : 'is-read'}`}
                                            aria-label={unread ? t('Notification non lue', 'Unread notification') : t('Notification lue', 'Read notification')}
                                          />
                                          <span className="user-overview-mini-date">{formatDateTime(notification?.created_at, dashboardLocale)}</span>
                                        </div>
                                        <button
                                          type="button"
                                          className="admin-expand user-overview-mini-action"
                                          onClick={() => {
                                            if (unread) markNotificationAsReadOptimistic(notification.id);
                                            openNotificationModal(notification);
                                          }}
                                        >
                                          {t('Voir plus', 'View details')}
                                        </button>
                                      </div>
                                      <div className="user-overview-mini-title">{notification?.title || t('Notification', 'Notification')}</div>
                                      <div className="user-overview-mini-snippet">
                                        {truncateSnippet(notification?.body || notification?.message || '', 120) || t('Aperçu du message indisponible.', 'Message preview unavailable.')}
                                      </div>
                                    </article>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          <div className="user-overview-side-card">
                            <div className="user-overview-side-head">
                              <h2 className="user-overview-side-title">{t('Aperçu des factures', 'Invoices overview')}</h2>
                              <button
                                type="button"
                                className="admin-page-btn"
                                onClick={() => setActiveTab('invoices')}
                              >
                                {t('Ouvrir', 'Open')}
                              </button>
                            </div>
                            <div className="user-overview-side-main">
                              {invoicesLoading
                                ? t('Chargement…', 'Loading...')
                                : isEnglishDashboard
                                  ? `${invoices.length} invoice${invoices.length === 1 ? '' : 's'}`
                                  : `${invoices.length} facture${invoices.length === 1 ? '' : 's'}`}
                            </div>
                            <div className="user-overview-side-sub">
                              {pendingInvoices.length > 1
                                ? isEnglishDashboard
                                  ? `${overviewInvoices.length} pending item${overviewInvoices.length === 1 ? '' : 's'} shown (max 5).`
                                  : `${overviewInvoices.length} élément${overviewInvoices.length === 1 ? '' : 's'} en attente affiché${overviewInvoices.length === 1 ? '' : 's'} (max 5).`
                                : t('Affichage de la dernière facture.', 'Showing the latest invoice.')}
                            </div>

                            <div className="user-overview-mini-list">
                              {invoicesLoading ? (
                                <div className="user-overview-mini-empty">{t('Chargement des factures…', 'Loading invoices...')}</div>
                              ) : overviewInvoices.length === 0 ? (
                                <div className="user-overview-mini-empty">{t('Aucune facture pour le moment.', 'No invoices at the moment.')}</div>
                              ) : (
                                overviewInvoices.map((invoice) => {
                                  const pending = isInvoicePendingStatus(invoice?.status);
                                  return (
                                    <article key={invoice.id} className="user-overview-mini-item">
                                      <div className="user-overview-mini-top">
                                        <div className="user-overview-mini-meta">
                                          <span
                                            className={`user-overview-mini-dot ${pending ? 'is-unread' : 'is-read'}`}
                                            aria-label={pending ? t('Facture en attente', 'Invoice pending') : t('Facture réglée', 'Invoice paid')}
                                          />
                                          <span className="user-overview-mini-date">{formatDateTime(invoice?.issued_at || invoice?.created_at, dashboardLocale)}</span>
                                        </div>
                                        <button
                                          type="button"
                                          className="admin-expand user-overview-mini-action"
                                          onClick={() => openInvoiceInTab(invoice)}
                                        >
                                          {t('Voir plus', 'View details')}
                                        </button>
                                      </div>
                                      <div className="user-overview-mini-title">{invoice?.invoice_number || `#${invoice?.id || '-'}`}</div>
                                      <div className="user-overview-mini-snippet">
                                        {isEnglishDashboard
                                          ? `Total: ${formatMoney(invoice?.total_amount, invoice?.currency)} • Status: ${String(invoice?.status || 'Unknown')}`
                                          : `Total : ${formatMoney(invoice?.total_amount, invoice?.currency)} • Statut : ${String(invoice?.status || 'Inconnu')}`}
                                      </div>
                                    </article>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          <div className="user-overview-side-card">
                            <div className="user-overview-side-head">
                              <h2 className="user-overview-side-title">{t('Demandes de documents', 'Document requests')}</h2>
                              <button
                                type="button"
                                className="admin-page-btn"
                                onClick={() => setActiveTab('document-requests')}
                              >
                                {t('Ouvrir', 'Open')}
                              </button>
                            </div>
                            <div className="user-overview-side-main">
                              {documentRequestsLoading
                                ? t('Chargement…', 'Loading...')
                                : isEnglishDashboard
                                  ? `${pendingDocumentRequestsCount} pending`
                                  : `${pendingDocumentRequestsCount} en attente`}
                            </div>
                            <div className="user-overview-side-sub">
                              {pendingDocumentRequests.length > 1
                                ? isEnglishDashboard
                                  ? `${overviewDocumentRequests.length} pending item${overviewDocumentRequests.length === 1 ? '' : 's'} shown (max 5).`
                                  : `${overviewDocumentRequests.length} élément${overviewDocumentRequests.length === 1 ? '' : 's'} en attente affiché${overviewDocumentRequests.length === 1 ? '' : 's'} (max 5).`
                                : t('Affichage du dernier statut de demande.', 'Showing the latest request status.')}
                            </div>

                            <div className="user-overview-mini-list">
                              {documentRequestsLoading ? (
                                <div className="user-overview-mini-empty">{t('Chargement des demandes…', 'Loading requests...')}</div>
                              ) : overviewDocumentRequests.length === 0 ? (
                                <div className="user-overview-mini-empty">{t('Aucune demande pour le moment.', 'No requests at the moment.')}</div>
                              ) : (
                                overviewDocumentRequests.map((request) => {
                                  const pending = String(request?.status || '').toLowerCase() !== 'completed';
                                  return (
                                    <article key={request.id} className="user-overview-mini-item">
                                      <div className="user-overview-mini-top">
                                        <div className="user-overview-mini-meta">
                                          <span
                                            className={`user-overview-mini-dot ${pending ? 'is-unread' : 'is-read'}`}
                                            aria-label={pending ? t('Demande en attente', 'Request pending') : t('Demande terminée', 'Request completed')}
                                          />
                                          <span className="user-overview-mini-date">{formatDateTime(request?.requested_at || request?.created_at, dashboardLocale)}</span>
                                        </div>
                                        <button
                                          type="button"
                                          className="admin-expand user-overview-mini-action"
                                          onClick={() => openDocumentRequestModal(request)}
                                        >
                                          {t('Voir plus', 'View details')}
                                        </button>
                                      </div>
                                      <div className="user-overview-mini-title">{pending ? t('Demande en attente', 'Request pending') : t('Demande terminée', 'Request completed')}</div>
                                      <div className="user-overview-mini-snippet">
                                        {isEnglishDashboard
                                          ? `${getDocumentRequestSummary(request, dashboardLanguage, dashboardLocale)} • Status: ${pending ? 'Pending' : 'Completed'}`
                                          : `${getDocumentRequestSummary(request, dashboardLanguage, dashboardLocale)} • Statut : ${pending ? 'En attente' : 'Terminée'}`}
                                      </div>
                                    </article>
                                  );
                                })
                              )}
                            </div>
                          </div>

                          <div className="user-overview-side-card user-overview-side-card--account">
                            <h2 className="user-overview-side-title">{t('Détails du compte', 'Account details')}</h2>
                            <div className="user-overview-kv">
                              <div className="user-overview-kv-row">
                                <span className="user-overview-k">{t('Contact', 'Contact')}</span>
                                <span className="user-overview-v">{user?.email || '-'}</span>
                              </div>
                              <div className="user-overview-kv-row">
                                <span className="user-overview-k">{t('Notifications non lues', 'Unread notifications')}</span>
                                <span className="user-overview-v">{unreadCount}</span>
                              </div>
                              <div className="user-overview-kv-row">
                                <span className="user-overview-k">{t('Total des factures', 'Total invoices')}</span>
                                <span className="user-overview-v">{invoices.length}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === 'invoices' && (
                    <div className="admin-page">
                      <div className="admin-page-head">
                        <h1 className="admin-page-title">{t('Factures', 'Invoices')}</h1>
                        <p className="admin-page-desc">
                          {t('Vos factures (générées automatiquement à partir des paiements réussis).', 'Your invoices (generated automatically from successful payments).')}
                        </p>
                      </div>

                      <div className="admin-page-toolbar">
                        <div className="admin-page-count">
                          {t('Toutes les factures', 'All invoices')} <span className="admin-page-count-number">{invoices.length}</span>
                        </div>
                        <div className="admin-page-controls">
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => fetchInvoices()}
                            disabled={invoicesLoading}
                          >
                            {invoicesLoading ? t('Actualisation…', 'Refreshing...') : t('Actualiser', 'Refresh')}
                          </button>
                        </div>
                      </div>

                      <div className="admin-table-wrap">
                        <table className="admin-table admin-invoices-table">
                          <thead>
                            <tr>
                              <th>{t('Facture', 'Invoice')}</th>
                              <th>{t('Émise le', 'Issued on')}</th>
                              <th>{t('Total', 'Total')}</th>
                              <th>{t('Statut', 'Status')}</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {invoicesLoading && invoices.length === 0 ? (
                              <tr><td colSpan={5} className="admin-table-empty">{t('Chargement…', 'Loading...')}</td></tr>
                            ) : invoices.length === 0 ? (
                              <tr><td colSpan={5} className="admin-table-empty">{t('Aucune facture pour le moment.', 'No invoices at the moment.')}</td></tr>
                            ) : (
                              pagedInvoices.map((inv) => {
                                return (
                                  <tr key={inv.id}>
                                    <td className="admin-cell-nowrap">
                                      <div className="admin-notif-title" title={inv.invoice_number || ''}>{inv.invoice_number || `#${inv.id}`}</div>
                                    </td>
                                    <td className="admin-cell-nowrap">{formatDate(inv.issued_at, dashboardLocale)}</td>
                                    <td className="admin-cell-nowrap">{formatMoney(inv.total_amount, inv.currency)}</td>
                                    <td className="admin-cell-nowrap">{String(inv.status || '-')}</td>
                                    <td className="admin-cell-actions">
                                      <button
                                        type="button"
                                        className="admin-expand"
                                        onClick={() => openInvoiceModal(inv)}
                                      >
                                        {t('Détails', 'Details')}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="admin-pagination-meta">
                        {invoices.length === 0
                          ? t('Affichage 0 sur 0', 'Showing 0 of 0')
                          : isEnglishDashboard
                            ? `Showing ${(invoicesPageClamped - 1) * invoicesPageSize + 1}-${Math.min(invoices.length, invoicesPageClamped * invoicesPageSize)} of ${invoices.length}`
                            : `Affichage ${(invoicesPageClamped - 1) * invoicesPageSize + 1}-${Math.min(invoices.length, invoicesPageClamped * invoicesPageSize)} sur ${invoices.length}`}
                      </div>

                      <div className="admin-pagination">
                        <button
                          type="button"
                          className="admin-page-btn"
                          onClick={() => setInvoicesPage((p) => Math.max(1, p - 1))}
                          disabled={invoicesPageClamped <= 1}
                        >
                          {t('Précédent', 'Previous')}
                        </button>
                        <div className="admin-page-numbers">
                          <button type="button" className="admin-page-number active" disabled>
                            {invoicesPageClamped} / {invoicesTotalPages}
                          </button>
                        </div>
                        <button
                          type="button"
                          className="admin-page-btn"
                          onClick={() => setInvoicesPage((p) => Math.min(invoicesTotalPages, p + 1))}
                          disabled={invoicesPageClamped >= invoicesTotalPages}
                        >
                          {t('Suivant', 'Next')}
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === 'notifications' && (
                    <div className="admin-page">
                      <div className="admin-page-head">
                        <h1 className="admin-page-title">{t('Notifications', 'Notifications')}</h1>
                        <p className="admin-page-desc">{t('Consultez les messages que vous avez reçus.', 'Review the messages you have received.')}</p>
                      </div>

                      {notificationsLoading ? (
                        <div className="admin-settings-empty">{t('Chargement des notifications...', 'Loading notifications...')}</div>
                      ) : (
                        <>
                          <div className="admin-page-toolbar">
                            <div className="admin-page-count">
                              {t('Toutes les notifications', 'All notifications')} <span className="admin-page-count-number">{notifications.length || 0}</span>
                            </div>

                            <div className="admin-page-controls">
                              <button
                                type="button"
                                className="admin-page-btn"
                                onClick={deleteSeenNotifications}
                                disabled={!notifications.some((n) => !!n.is_read)}
                              >
                                {t('Supprimer toutes les lues', 'Delete all read')}
                              </button>
                            </div>
                          </div>

                          <div className="admin-table-wrap">
                            <table className="admin-table user-notif-table">
                              <thead>
                                <tr>
                                  <th>{t('Titre', 'Title')}</th>
                                  <th>{t('Reçue le', 'Received on')}</th>
                                  <th>{t('Statut', 'Status')}</th>
                                  <th></th>
                                </tr>
                              </thead>
                              <tbody>
                                  {notifications.length > 0 ? (
                                    pagedNotifications.map((n) => {
                                    const unread = !n.is_read;
                                      const isExpanded = showNotificationModal && selectedNotification?.id === n.id;
                                    return (
                                      <tr key={n.id}>
                                        <td className="user-notif-title">{n.title || '-'}</td>
                                        <td className="admin-cell-nowrap">{formatDateTime(n.created_at, dashboardLocale)}</td>
                                        <td className="admin-cell-nowrap">
                                          <div className="user-notif-status">
                                            <span
                                              className={`user-notif-dot ${unread ? 'user-notif-dot--unread' : 'user-notif-dot--seen'}`}
                                              aria-label={unread ? t('Non lue', 'Unread') : t('Lue', 'Read')}
                                            />
                                          </div>
                                        </td>
                                        <td className="admin-cell-actions">
                                          <button
                                            type="button"
                                            className="admin-expand"
                                              aria-expanded={isExpanded}
                                            onClick={() => {
                                              if (unread) markNotificationAsReadOptimistic(n.id);
                                              openNotificationModal(n);
                                            }}
                                          >
                                              {t('Développer', 'Expand')}
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })
                                ) : (
                                  <tr>
                                    <td colSpan={4} className="admin-settings-empty">{t('Aucune notification.', 'No notifications.')}</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                            <div className="admin-pagination-meta">
                              {notifications.length === 0
                                ? t('Affichage 0 sur 0', 'Showing 0 of 0')
                                : isEnglishDashboard
                                  ? `Showing ${(notificationsPageClamped - 1) * notificationsPageSize + 1}-${Math.min(notifications.length, notificationsPageClamped * notificationsPageSize)} of ${notifications.length}`
                                  : `Affichage ${(notificationsPageClamped - 1) * notificationsPageSize + 1}-${Math.min(notifications.length, notificationsPageClamped * notificationsPageSize)} sur ${notifications.length}`}
                            </div>

                            <div className="admin-pagination">
                              <button
                                type="button"
                                className="admin-page-btn"
                                onClick={() => setNotificationsPage((p) => Math.max(1, p - 1))}
                                disabled={notificationsPageClamped <= 1}
                              >
                                {t('Précédent', 'Previous')}
                              </button>
                              <div className="admin-page-numbers">
                                {getPaginationItems(notificationsTotalPages, notificationsPageClamped).map((item, idx) => {
                                  if (item === '…') {
                                    return (
                                      <button
                                        key={`dots-user-notif-${idx}`}
                                        type="button"
                                        className="admin-page-number"
                                        disabled
                                        aria-hidden="true"
                                      >
                                        …
                                      </button>
                                    );
                                  }
                                  const pageNum = item;
                                  const isActive = pageNum === notificationsPageClamped;
                                  return (
                                    <button
                                      key={pageNum}
                                      type="button"
                                      className={`admin-page-number ${isActive ? 'active' : ''}`}
                                      onClick={() => setNotificationsPage(pageNum)}
                                    >
                                      {pageNum}
                                    </button>
                                  );
                                })}
                              </div>
                              <button
                                type="button"
                                className="admin-page-btn"
                                onClick={() => setNotificationsPage((p) => Math.min(notificationsTotalPages, p + 1))}
                                disabled={notificationsPageClamped >= notificationsTotalPages}
                              >
                                {t('Suivant', 'Next')}
                              </button>
                            </div>
                        </>
                      )}
                    </div>
                  )}

                  {activeTab === 'document-requests' && (
                    <div className="admin-page">
                      <div className="admin-page-head">
                        <h1 className="admin-page-title">{t('Demande de documents', 'Document requests')}</h1>
                        <p className="admin-page-desc">{t('Une seule demande à la fois. Nouvelle demande possible tous les 10 jours.', 'Only one request at a time. A new request is possible every 10 days.')}</p>
                      </div>

                      <div className="admin-page-toolbar">
                        <div className="admin-page-count">
                          {t('Demandes', 'Requests')} <span className="admin-page-count-number">{documentRequests.length}</span>
                        </div>

                        <div className="admin-page-controls" style={{ gap: '0.6rem' }}>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => {
                              setShowDocumentRequestComposer(true);
                              setDocumentRequestNotice('');
                            }}
                          >
                            {t('Nouvelle demande', 'New request')}
                          </button>
                        </div>
                      </div>

                      {documentRequestNotice ? (
                        <div className="admin-settings-empty" style={{ color: '#222' }}>{documentRequestNotice}</div>
                      ) : null}

                      <div className="admin-table-wrap">
                        <table className="admin-table user-notif-table">
                          <thead>
                            <tr>
                              <th>{t('Admin', 'Admin')}</th>
                              <th>{t('Date demandée', 'Requested on')}</th>
                              <th>{t('Date reçue', 'Received on')}</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {documentRequestsLoading && documentRequests.length === 0 ? (
                              <tr><td colSpan={4} className="admin-table-empty">{t('Chargement…', 'Loading...')}</td></tr>
                            ) : documentRequests.length === 0 ? (
                              <tr><td colSpan={4} className="admin-table-empty">{t('Aucune demande pour le moment.', 'No requests at the moment.')}</td></tr>
                            ) : (
                              pagedDocumentRequests.map((r) => {
                                const pending = String(r.status || '') !== 'completed';
                                return (
                                  <tr key={r.id}>
                                    <td className="user-notif-title">{t('Admin', 'Admin')}</td>
                                    <td className="admin-cell-nowrap">{formatDateTime(r.requested_at, dashboardLocale)}</td>
                                    <td className="admin-cell-nowrap">
                                      {pending ? t('En attente', 'Pending') : formatDateTime(r.completed_at, dashboardLocale)}
                                    </td>
                                    <td className="admin-cell-actions">
                                      <button
                                        type="button"
                                        className="admin-expand"
                                        onClick={() => openDocumentRequestModal(r)}
                                      >
                                        {t('Détails', 'Details')}
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="admin-pagination">
                        <button
                          type="button"
                          className="admin-page-btn"
                          onClick={() => setDocumentRequestsPage((p) => Math.max(1, p - 1))}
                          disabled={documentRequestsPageClamped <= 1}
                        >
                          {t('Précédent', 'Previous')}
                        </button>
                        <div className="admin-page-numbers">
                          {getPaginationItems(documentRequestsTotalPages, documentRequestsPageClamped).map((item, idx) => {
                            if (item === '…') {
                              return (
                                <button
                                  key={`dots-user-docreq-${idx}`}
                                  type="button"
                                  className="admin-page-number"
                                  disabled
                                  aria-hidden="true"
                                >
                                  …
                                </button>
                              );
                            }
                            const pageNum = item;
                            const isActive = pageNum === documentRequestsPageClamped;
                            return (
                              <button
                                key={pageNum}
                                type="button"
                                className={`admin-page-number ${isActive ? 'active' : ''}`}
                                onClick={() => setDocumentRequestsPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="admin-page-btn"
                          onClick={() => setDocumentRequestsPage((p) => Math.min(documentRequestsTotalPages, p + 1))}
                          disabled={documentRequestsPageClamped >= documentRequestsTotalPages}
                        >
                          {t('Suivant', 'Next')}
                        </button>
                      </div>
                    </div>
                  )}

                  {activeTab === 'house-management' && (
                    <div className="admin-page">
                      <div className="admin-page-head">
                        <h1 className="admin-page-title">{t('Gestion du logement', 'Housing management')}</h1>
                        <p className="admin-page-desc">{t('Une demande tous les 10 jours. Pour un changement de residence, selectionnez une seule residence.', 'One request every 10 days. For a residence change, select only one residence.')}</p>
                      </div>

                      <div className="admin-page-toolbar">
                        <div className="admin-page-count">
                          {t('Demandes', 'Requests')} <span className="admin-page-count-number">{houseRequests.length}</span>
                        </div>

                        <div className="admin-page-controls" style={{ gap: '0.55rem', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => {
                              setShowLeaveResidenceModal(false);
                              setShowChangeResidenceModal(true);
                            }}
                          >
                            {t('Changer de residence', 'Change residence')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline"
                            onClick={() => {
                              setShowChangeResidenceModal(false);
                              setShowLeaveResidenceModal(true);
                            }}
                          >
                            {t('Fin de contrat', 'End contract')}
                          </button>
                        </div>
                      </div>

                      {houseRequestNotice ? (
                        <div className="admin-settings-empty" style={{ color: '#222' }}>{houseRequestNotice}</div>
                      ) : null}

                      <div className="admin-table-wrap">
                        <table className="admin-table user-notif-table">
                          <thead>
                            <tr>
                              <th>{t('Type', 'Type')}</th>
                              <th>{t('Soumis le', 'Submitted on')}</th>
                              <th>{t('Statut', 'Status')}</th>
                              <th>{t('Vu admin', 'Seen by admin')}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {houseRequestsLoading && houseRequests.length === 0 ? (
                              <tr><td colSpan={4} className="admin-table-empty">{t('Chargement...', 'Loading...')}</td></tr>
                            ) : houseRequests.length === 0 ? (
                              <tr><td colSpan={4} className="admin-table-empty">{t('Aucune demande pour le moment.', 'No requests at the moment.')}</td></tr>
                            ) : (
                              pagedHouseRequests.map((request) => {
                                const isSeenByAdmin = Number(request.is_seen) === 1;
                                const requestTypeLabel = request.request_type === 'change_residence'
                                  ? t('Changement de residence', 'Residence change')
                                  : t('Fin de contrat', 'End contract');
                                const requestedDateLabel = request.request_type === 'change_residence'
                                  ? t('Date de demenagement souhaitee', 'Preferred move date')
                                  : t('Date de depart souhaitee', 'Preferred leave date');
                                const requestedDateValue = request.request_type === 'change_residence'
                                  ? request.requested_move_date
                                  : request.requested_leave_date;
                                return (
                                  <tr key={request.id}>
                                    <td className="user-notif-title">
                                      <div>{requestTypeLabel}</div>
                                      <div style={{ fontSize: '0.82rem', color: '#666' }}>
                                        {requestedDateValue
                                          ? `${requestedDateLabel}: ${formatDate(requestedDateValue, dashboardLocale)}`
                                          : truncateSnippet(request.reason || '', 90)}
                                      </div>
                                    </td>
                                    <td className="admin-cell-nowrap">{formatDateTime(request.created_at, dashboardLocale)}</td>
                                    <td className="admin-cell-nowrap">{String(request.status || 'pending')}</td>
                                    <td className="admin-cell-nowrap">
                                      <div className="user-notif-status">
                                        <span
                                          className={`user-notif-dot ${isSeenByAdmin ? 'user-notif-dot--seen' : 'user-notif-dot--unread'}`}
                                          aria-label={isSeenByAdmin ? t('Vu par un admin', 'Seen by an admin') : t('Pas encore vu par un admin', 'Not yet seen by an admin')}
                                        />
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="admin-pagination">
                        <button
                          type="button"
                          className="admin-page-btn"
                          onClick={() => setHouseRequestsPage((p) => Math.max(1, p - 1))}
                          disabled={houseRequestsPageClamped <= 1}
                        >
                          {t('Precedent', 'Previous')}
                        </button>
                        <div className="admin-page-numbers">
                          {getPaginationItems(houseRequestsTotalPages, houseRequestsPageClamped).map((item, idx) => {
                            if (item === '…') {
                              return (
                                <button
                                  key={`dots-user-house-${idx}`}
                                  type="button"
                                  className="admin-page-number"
                                  disabled
                                  aria-hidden="true"
                                >
                                  …
                                </button>
                              );
                            }
                            const pageNum = item;
                            const isActive = pageNum === houseRequestsPageClamped;
                            return (
                              <button
                                key={pageNum}
                                type="button"
                                className={`admin-page-number ${isActive ? 'active' : ''}`}
                                onClick={() => setHouseRequestsPage(pageNum)}
                              >
                                {pageNum}
                              </button>
                            );
                          })}
                        </div>
                        <button
                          type="button"
                          className="admin-page-btn"
                          onClick={() => setHouseRequestsPage((p) => Math.min(houseRequestsTotalPages, p + 1))}
                          disabled={houseRequestsPageClamped >= houseRequestsTotalPages}
                        >
                          {t('Suivant', 'Next')}
                        </button>
                      </div>
                    </div>
                  )}

                    {activeTab === 'settings' && (
                      <div className="admin-page user-settings-page">
                        <div className="admin-page-head">
                          <h1 className="admin-page-title">{t('Paramètres', 'Settings')}</h1>
                          <p className="admin-page-desc">
                            {t('Consultez vos informations et mettez à jour uniquement votre mot de passe ou votre numéro.', 'Review your information and update only your password or phone number.')}
                          </p>
                        </div>

                        <div className="user-settings-grid">
                          <section className="user-settings-card">
                            <h2 className="user-settings-title">{t('Langue du dashboard', 'Dashboard language')}</h2>
                            <p className="user-settings-subtitle">{t('Choisissez la langue d\'affichage du tableau de bord.', 'Choose the display language for your dashboard.')}</p>

                            <div className="user-settings-language-switch" role="group" aria-label={t('Sélection de la langue', 'Language selection')}>
                              <button
                                type="button"
                                className={`user-settings-language-option ${dashboardLanguage === 'fr' ? 'is-active' : ''}`}
                                onClick={() => setDashboardLanguage('fr')}
                              >
                                Francais
                              </button>
                              <button
                                type="button"
                                className={`user-settings-language-option ${dashboardLanguage === 'en' ? 'is-active' : ''}`}
                                onClick={() => setDashboardLanguage('en')}
                              >
                                English
                              </button>
                            </div>
                          </section>

                          <section className="user-settings-card">
                            <h2 className="user-settings-title">{t('Informations personnelles', 'Personal information')}</h2>
                            <p className="user-settings-subtitle">{t('Ces champs sont en lecture seule.', 'These fields are read-only.')}</p>

                            <div className="user-settings-readonly-grid">
                              {settingsReadOnlyItems.map((item) => (
                                <div key={item.label} className="user-settings-readonly-item">
                                  <span className="user-settings-readonly-label">{item.label}</span>
                                  <span className="user-settings-readonly-value">{item.value || '-'}</span>
                                </div>
                              ))}
                            </div>
                          </section>

                          <section className="user-settings-card">
                            <div className="user-settings-action-head">
                              <div>
                                <h2 className="user-settings-title">{t('Mot de passe', 'Password')}</h2>
                                <p className="user-settings-subtitle">{t('Recevez un code par email puis définissez un nouveau mot de passe.', 'Receive a code by email, then set a new password.')}</p>
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={openPasswordSettingsPanel}
                                disabled={isPhonePanelOpen}
                              >
                                {t('Modifier', 'Edit')}
                              </button>
                            </div>

                            <p className="user-settings-hint user-settings-overlay-note">
                              {t('L\'édition s\'ouvre dans une fenêtre superposée, comme dans l\'onglet des factures.', 'Editing opens in an overlay window, like in the invoices tab.')}
                            </p>
                          </section>

                          <section className="user-settings-card">
                            <div className="user-settings-action-head">
                              <div>
                                <h2 className="user-settings-title">{t('Numéro de téléphone', 'Phone number')}</h2>
                                <p className="user-settings-subtitle">{t('Saisissez un nouveau numéro, puis confirmez avant enregistrement.', 'Enter a new number, then confirm before saving.')}</p>
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={openPhoneSettingsPanel}
                                disabled={isPasswordPanelOpen}
                              >
                                {t('Modifier', 'Edit')}
                              </button>
                            </div>

                            <p className="user-settings-hint user-settings-overlay-note">
                              {t('L\'édition s\'ouvre dans une fenêtre superposée, comme dans l\'onglet des factures.', 'Editing opens in an overlay window, like in the invoices tab.')}
                            </p>
                          </section>
                        </div>
                      </div>
                    )}
                </div>
              </CSSTransition>
            </TransitionGroup>
          </div>
        </main>
      </div>

      {activeTab === 'settings' && isPasswordPanelOpen && (
        <div className="modal-overlay user-settings-modal-overlay">
          <div className="modal-content modal-wide modal-zoom-in user-settings-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('Modifier le mot de passe', 'Change password')}</h3>
            </div>

            <div className="modal-body">
              <div className="user-settings-expand user-settings-expand--locked user-settings-modal-body">
                <p className="user-settings-hint">{t('Cette section reste ouverte jusqu\'à annulation explicite.', 'This section stays open until you explicitly cancel.')}</p>

                {passwordMessage ? <div className="user-settings-success">{passwordMessage}</div> : null}
                {passwordError ? <div className="user-settings-error">{passwordError}</div> : null}

                <div className="user-settings-inline-row">
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={sendPasswordResetOtp}
                    disabled={passwordBusyState === 'sending' || passwordCooldown > 0}
                  >
                    {passwordBusyState === 'sending'
                      ? t('Envoi...', 'Sending...')
                      : passwordCooldown > 0
                        ? (isEnglishDashboard ? `Resend in ${passwordCooldown}s` : `Renvoyer dans ${passwordCooldown}s`)
                        : t('Envoyer le code', 'Send code')}
                  </button>
                </div>

                <div className="admin-form-2col">
                  <div className="form-group">
                    <label>{t('Code OTP', 'OTP code')}</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={passwordOtp}
                      onChange={(e) => setPasswordOtp(normalizePhoneDigits(e.target.value).slice(0, 6))}
                      placeholder={t('6 chiffres', '6 digits')}
                    />
                  </div>
                  <div className="form-group user-settings-otp-action">
                    <label>{t('Vérification', 'Verification')}</label>
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={verifyPasswordResetCode}
                      disabled={passwordBusyState === 'verifying' || String(passwordOtp || '').trim().length !== 6}
                    >
                      {passwordBusyState === 'verifying' ? t('Vérification...', 'Verifying...') : t('Confirmer le code', 'Confirm code')}
                    </button>
                  </div>
                </div>

                {passwordResetToken ? (
                  <>
                    <div className="admin-form-2col">
                      <div className="form-group">
                        <label>{t('Nouveau mot de passe', 'New password')}</label>
                        <input
                          type="password"
                          value={passwordNewValue}
                          onChange={(e) => setPasswordNewValue(e.target.value)}
                          autoComplete="new-password"
                        />
                      </div>
                      <div className="form-group">
                        <label>{t('Confirmer le mot de passe', 'Confirm password')}</label>
                        <input
                          type="password"
                          value={passwordConfirmValue}
                          onChange={(e) => setPasswordConfirmValue(e.target.value)}
                          autoComplete="new-password"
                        />
                      </div>
                    </div>

                    <div className="user-settings-actions">
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={cancelPasswordSettingsPanel}
                        disabled={passwordBusyState === 'saving'}
                      >
                        {t('Annuler', 'Cancel')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={submitNewPasswordFromSettings}
                        disabled={passwordBusyState === 'saving'}
                      >
                        {passwordBusyState === 'saving' ? t('Mise à jour...', 'Updating...') : t('Confirmer le nouveau mot de passe', 'Confirm new password')}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="user-settings-actions">
                    <button
                      type="button"
                      className="btn btn-outline"
                      onClick={cancelPasswordSettingsPanel}
                      disabled={Boolean(passwordBusyState)}
                    >
                      {t('Annuler', 'Cancel')}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && isPhonePanelOpen && (
        <div className="modal-overlay user-settings-modal-overlay">
          <div className="modal-content modal-wide modal-zoom-in user-settings-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('Modifier le numéro de téléphone', 'Change phone number')}</h3>
            </div>

            <div className="modal-body">
              <div className="user-settings-expand user-settings-expand--locked user-settings-modal-body">
                <p className="user-settings-hint">{t('Cette section reste ouverte jusqu\'à annulation explicite.', 'This section stays open until you explicitly cancel.')}</p>

                {phoneMessage ? <div className="user-settings-success">{phoneMessage}</div> : null}
                {phoneError ? <div className="user-settings-error">{phoneError}</div> : null}

                {!phoneConfirmStage ? (
                  <>
                    <div className="form-group">
                      <label>{t('Nouveau numéro (chiffres uniquement)', 'New number (digits only)')}</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={phoneDraft}
                        onChange={(e) => setPhoneDraft(normalizePhoneDigits(e.target.value).slice(0, 20))}
                        placeholder="Ex: 0612345678"
                      />
                    </div>

                    <div className="user-settings-actions">
                      <button type="button" className="btn btn-outline" onClick={cancelPhoneSettingsPanel}>
                        {t('Annuler', 'Cancel')}
                      </button>
                      <button type="button" className="btn btn-primary" onClick={continuePhoneConfirmation}>
                        {t('Continuer', 'Continue')}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="user-settings-confirm-box">
                      <span className="user-settings-confirm-label">{t('Confirmer ce numéro', 'Confirm this number')}</span>
                      <span className="user-settings-confirm-value">{phoneDraft}</span>
                    </div>

                    <div className="user-settings-actions">
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={() => setPhoneConfirmStage(false)}
                        disabled={phoneBusy}
                      >
                        {t('Modifier', 'Edit')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline"
                        onClick={cancelPhoneSettingsPanel}
                        disabled={phoneBusy}
                      >
                        {t('Annuler', 'Cancel')}
                      </button>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={confirmPhoneUpdate}
                        disabled={phoneBusy}
                      >
                        {phoneBusy ? t('Enregistrement...', 'Saving...') : t('Confirmer le numéro', 'Confirm number')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showChangeResidenceModal && (
        <div className="modal-overlay" onClick={closeChangeResidenceModal}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('Demande de changement de residence', 'Residence change request')}</h3>
              <button className="modal-close" onClick={closeChangeResidenceModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>{t('Date de demenagement souhaitee', 'Preferred move date')}</label>
                <input
                  type="date"
                  value={changeResidenceForm.requested_move_date}
                  min={minRequestedHouseDate}
                  onChange={(e) => setChangeResidenceForm((prev) => ({ ...prev, requested_move_date: e.target.value }))}
                />
              </div>

              <div className="admin-form-2col">
                <div className="form-group">
                  <label>{t('Ville souhaitee', 'Preferred city')}</label>
                  <select
                    value={changeResidenceForm.desired_city}
                    onChange={(e) => {
                      const city = e.target.value;
                      setChangeResidenceForm((prev) => ({
                        ...prev,
                        desired_city: city,
                        desired_region: '',
                        desired_residences: [],
                      }));
                    }}
                  >
                    <option value="">{t('Selectionner', 'Select')}</option>
                    {houseCities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>

                {changeResidenceForm.desired_city === 'Paris' && (
                  <div className="form-group">
                    <label>{t('Sous-region Paris', 'Paris sub-region')}</label>
                    <select
                      value={changeResidenceForm.desired_region}
                      onChange={(e) => {
                        const region = e.target.value;
                        setChangeResidenceForm((prev) => ({
                          ...prev,
                          desired_region: region,
                          desired_residences: [],
                        }));
                      }}
                    >
                      <option value="">{t('Selectionner', 'Select')}</option>
                      {houseParisSubRegions.map((region) => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {availableChangeResidences.length > 0 && (
                <div className="user-house-residences-section">
                  <label>
                    {t('Residence souhaitee', 'Preferred residence')} <span className="required">*</span> <span className="hint">(1 seul choix)</span>
                  </label>
                  <div className="user-house-residences-grid">
                    {availableChangeResidences.map((residence) => {
                      const selected = residence === selectedDesiredResidence;
                      return (
                        <label key={residence} className={`user-house-residence-option ${selected ? 'selected' : ''}`}>
                          <input
                            type="radio"
                            name="preferred-residence"
                            checked={selected}
                            onChange={() => selectDesiredResidence(residence)}
                          />
                          <span>{residence}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={closeChangeResidenceModal}
                disabled={houseRequestsSubmitting}
              >
                {t('Annuler', 'Cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitChangeResidenceRequest}
                disabled={houseRequestsSubmitting}
              >
                {houseRequestsSubmitting ? t('Envoi...', 'Sending...') : t('Envoyer la demande', 'Send request')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showLeaveResidenceModal && (
        <div className="modal-overlay" onClick={closeLeaveResidenceModal}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('Demande de fin de contrat', 'End-of-contract request')}</h3>
              <button className="modal-close" onClick={closeLeaveResidenceModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>{t('Date de depart souhaitee', 'Preferred leave date')}</label>
                <input
                  type="date"
                  value={leaveResidenceForm.requested_leave_date}
                  min={minRequestedHouseDate}
                  onChange={(e) => setLeaveResidenceForm((prev) => ({ ...prev, requested_leave_date: e.target.value }))}
                />
              </div>

              <label className="user-house-confirm-row">
                <input
                  type="checkbox"
                  checked={Boolean(leaveResidenceForm.confirm_termination)}
                  onChange={(e) => setLeaveResidenceForm((prev) => ({ ...prev, confirm_termination: e.target.checked }))}
                />
                <span>{t('Je confirme vouloir engager une procedure de fin de contrat / depart de residence.', 'I confirm that I want to start an end-of-contract / residence departure process.')}</span>
              </label>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={closeLeaveResidenceModal}
                disabled={houseRequestsSubmitting}
              >
                {t('Annuler', 'Cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitLeaveResidenceRequest}
                disabled={houseRequestsSubmitting}
              >
                {houseRequestsSubmitting ? t('Envoi...', 'Sending...') : t('Envoyer la demande', 'Send request')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDocumentRequestComposer && (
        <div className="modal-overlay" onClick={resetDocumentRequestComposer}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('Créer une demande', 'Create a request')}</h3>
              <button className="modal-close" onClick={resetDocumentRequestComposer}>×</button>
            </div>

            <div className="modal-body">
              <p style={{ marginTop: 0 }}>{t('Choisissez un seul document. Pour la quittance de loyer, indiquez la période.', 'Choose a single document. For rent receipts, indicate a period.')}</p>

              <div className="user-docreq-options">
                {DOC_REQUEST_TYPES.map((docType) => {
                  const isSelected = documentRequestForm.selected_type === docType.key;
                  return (
                    <label key={docType.key} className={`user-docreq-option ${isSelected ? 'is-selected' : ''}`}>
                      <input
                        type="radio"
                        name="document-request-type"
                        value={docType.key}
                        checked={isSelected}
                        onChange={() => {
                          setDocumentRequestForm((prev) => ({
                            ...prev,
                            selected_type: docType.key,
                            caf_period_from_month: docType.key === 'quittance_de_loyer' ? prev.caf_period_from_month : '',
                            caf_period_till_month: docType.key === 'quittance_de_loyer' ? prev.caf_period_till_month : '',
                          }));
                        }}
                      />
                      <span>{getDocumentTypeLabel(docType, dashboardLanguage)}</span>
                    </label>
                  );
                })}
              </div>

              {documentRequestForm.selected_type === 'quittance_de_loyer' && (
                <div className="user-docreq-caf-range">
                  {eligibleCafMonths.length === 0 ? (
                    <div className="user-docreq-caf-empty">
                      {t('Aucune période disponible. La période doit aller du mois suivant votre date d\'entrée au dernier mois terminé.', 'No period available. The range must start from the month after your move-in date and end at the last completed month.')}
                    </div>
                  ) : (
                    <div className="admin-form-2col">
                      <div className="form-group">
                        <label>{t('De', 'From')}</label>
                        <select
                          value={documentRequestForm.caf_period_from_month}
                          onChange={(e) => {
                            const nextFrom = e.target.value;
                            setDocumentRequestForm((prev) => {
                              const keepTill = prev.caf_period_till_month && prev.caf_period_till_month >= nextFrom
                                ? prev.caf_period_till_month
                                : '';
                              return {
                                ...prev,
                                caf_period_from_month: nextFrom,
                                caf_period_till_month: keepTill,
                              };
                            });
                          }}
                        >
                          <option value="">{t('Sélectionner', 'Select')}</option>
                          {eligibleCafMonths.map((monthOpt) => (
                            <option key={monthOpt.key} value={monthOpt.key}>{monthOpt.label}</option>
                          ))}
                        </select>
                      </div>

                      <div className="form-group">
                        <label>{t('À', 'To')}</label>
                        <select
                          value={documentRequestForm.caf_period_till_month}
                          onChange={(e) => setDocumentRequestForm((prev) => ({
                            ...prev,
                            caf_period_till_month: e.target.value,
                          }))}
                          disabled={!documentRequestForm.caf_period_from_month}
                        >
                          <option value="">{t('Sélectionner', 'Select')}</option>
                          {cafTillMonthOptions.map((monthOpt) => (
                            <option key={monthOpt.key} value={monthOpt.key}>{monthOpt.label}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={resetDocumentRequestComposer}
                disabled={documentRequestsSubmitting}
              >
                {t('Annuler', 'Cancel')}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={submitDocumentRequest}
                disabled={documentRequestsSubmitting}
              >
                {documentRequestsSubmitting ? t('Envoi...', 'Sending...') : t('Envoyer la demande', 'Send request')}
              </button>
            </div>
          </div>
        </div>
      )}

      {showInvoiceModal && selectedInvoice && (
        <div className="modal-overlay" onClick={closeInvoiceModal}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('Facture', 'Invoice')} {selectedInvoice.invoice_number || `#${selectedInvoice.id || '-'}`}</h3>
              <button className="modal-close" onClick={closeInvoiceModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="admin-expanded-grid admin-invoice-expanded-grid">
                <div>
                  <span className="admin-k">{t('Facture', 'Invoice')}</span>
                  <span className="admin-v">{selectedInvoice.invoice_number || '-'}</span>
                </div>
                <div>
                  <span className="admin-k">{t('Émise le', 'Issued on')}</span>
                  <span className="admin-v">{formatDateTime(selectedInvoice.issued_at, dashboardLocale)}</span>
                </div>
                <div>
                  <span className="admin-k">{t('Payée le', 'Paid on')}</span>
                  <span className="admin-v">{formatDateTime(selectedInvoice.paid_at, dashboardLocale)}</span>
                </div>
                <div>
                  <span className="admin-k">{t('Référence de paiement', 'Payment reference')}</span>
                  <span className="admin-v">{selectedInvoice.gocardless_payment_id || '-'}</span>
                </div>
                <div className="admin-invoice-lines">
                  <span className="admin-k">{t('Lignes', 'Line items')}</span>
                  <div className="admin-v">
                    {Array.isArray(selectedInvoice.items) && selectedInvoice.items.length ? (
                      <table className="admin-mini-table">
                        <tbody>
                          {selectedInvoice.items.map((it) => (
                            <tr key={it.id || it.label}>
                              <td>{it.label}</td>
                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMoney(it.amount, selectedInvoice.currency)}</td>
                            </tr>
                          ))}
                          <tr className="admin-mini-total">
                            <td>{t('Total', 'Total')}</td>
                            <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{formatMoney(selectedInvoice.total_amount, selectedInvoice.currency)}</td>
                          </tr>
                        </tbody>
                      </table>
                    ) : (
                      <span>-</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-outline"
                onClick={() => {
                  api.invoices.getMyInvoicePdf(selectedInvoice.id)
                    .then((response) => {
                      const fileUrl = response?.data?.data?.fileUrl;
                      const resolvedUrl = resolveMediaUrl(fileUrl);
                      if (!resolvedUrl) {
                        alert(t('Impossible de generer le PDF de la facture.', 'Unable to generate the invoice PDF.'));
                        return;
                      }
                      const w = window.open(resolvedUrl, '_blank');
                      if (!w) {
                        alert(t('Popup bloquée. Veuillez autoriser les popups pour imprimer.', 'Popup blocked. Please allow popups to print.'));
                      }
                    })
                    .catch((error) => {
                      console.error('Failed to open invoice PDF:', error);
                      alert(error?.response?.data?.error || t('Echec de l\'ouverture du PDF.', 'Failed to open the PDF.'));
                    });
                }}
              >
                {t('Imprimer', 'Print')}
              </button>
              <button className="btn btn-primary" onClick={closeInvoiceModal}>{t('Fermer', 'Close')}</button>
            </div>
          </div>
        </div>
      )}

      {showDocumentRequestModal && selectedDocumentRequest && (() => {
        const pending = String(selectedDocumentRequest.status || '') !== 'completed';
        const docs = DOC_REQUEST_TYPES
          .filter((docType) => Number(selectedDocumentRequest?.[docType.requestField]) === 1)
          .map((docType) => {
            const isQuittance = docType.key === 'quittance_de_loyer';
            const period = isQuittance
              ? formatCafPeriodLabel(selectedDocumentRequest?.caf_period_from_month, selectedDocumentRequest?.caf_period_till_month, dashboardLocale)
              : '';
            return {
              key: docType.key,
              label: getDocumentTypeLabel(docType, dashboardLanguage),
              meta: period ? (isEnglishDashboard ? `Period: ${period}` : `Période: ${period}`) : '',
              url: selectedDocumentRequest?.[docType.fileField],
            };
          });

        return (
          <div className="modal-overlay" onClick={closeDocumentRequestModal}>
            <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{t('Demande de documents', 'Document request')}</h3>
                <button className="modal-close" onClick={closeDocumentRequestModal}>×</button>
              </div>

              <div className="modal-body">
                <div className="admin-expanded-grid">
                  <div><span className="admin-k">{t('Admin', 'Admin')}</span><span className="admin-v">{t('Admin', 'Admin')}</span></div>
                  <div><span className="admin-k">{t('Date demandée', 'Requested on')}</span><span className="admin-v">{formatDateTime(selectedDocumentRequest.requested_at, dashboardLocale)}</span></div>
                  <div><span className="admin-k">{t('Date reçue', 'Received on')}</span><span className="admin-v">{pending ? t('En attente', 'Pending') : formatDateTime(selectedDocumentRequest.completed_at, dashboardLocale)}</span></div>
                </div>

                <div style={{ marginTop: '1rem' }}>
                  <h4>{t('Détails', 'Details')}</h4>
                  {docs.length === 0 ? (
                    <p>{t('Aucun document demandé.', 'No document requested.')}</p>
                  ) : (
                    <div className="admin-table-wrap">
                      <table className="admin-table">
                        <thead>
                          <tr>
                            <th>{t('Document', 'Document')}</th>
                            <th>{t('Statut', 'Status')}</th>
                            <th>{t('Téléchargement', 'Download')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {docs.map((d) => {
                            const hasFile = Boolean(d.url);
                            return (
                              <tr key={d.key}>
                                <td>
                                  <div>{d.label}</div>
                                  {d.meta ? <div style={{ fontSize: '0.82rem', color: '#666' }}>{d.meta}</div> : null}
                                </td>
                                <td className="admin-cell-nowrap">{hasFile ? t('Disponible', 'Available') : t('En attente', 'Pending')}</td>
                                <td>
                                  {hasFile ? (
                                    <a className="admin-link" href={resolveMediaUrl(d.url) || '#'} target="_blank" rel="noreferrer">
                                      {t('Voir / Télécharger', 'View / Download')}
                                    </a>
                                  ) : (
                                    <span>-</span>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn btn-outline" onClick={closeDocumentRequestModal}>{t('Fermer', 'Close')}</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showNotificationModal && selectedNotification && (
        <div className="modal-overlay" onClick={closeNotificationModal}>
          <div className="modal-content modal-wide modal-zoom-in" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('Notification', 'Notification')}</h3>
              <button className="modal-close" onClick={closeNotificationModal}>×</button>
            </div>

            <div className="modal-body">
              <div className="admin-expanded-grid">
                <div>
                  <span className="admin-k">{t('Titre', 'Title')}</span>
                  <span className="admin-v">{selectedNotification.title || '-'}</span>
                </div>
                <div>
                  <span className="admin-k">{t('Reçue le', 'Received on')}</span>
                  <span className="admin-v">{formatDateTime(selectedNotification.created_at, dashboardLocale)}</span>
                </div>
                <div>
                  <span className="admin-k">{t('Statut', 'Status')}</span>
                  <span className="admin-v">{selectedNotification.is_read ? t('Lue', 'Read') : t('Non lue', 'Unread')}</span>
                </div>
              </div>

              <div className="user-notif-message">
                {selectedNotification.body || '-'}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-danger"
                onClick={() => {
                  deleteNotification(selectedNotification.id);
                  closeNotificationModal();
                }}
              >
                {t('Supprimer la notification', 'Delete notification')}
              </button>
              <button className="btn btn-outline" onClick={closeNotificationModal}>{t('Fermer', 'Close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDashboard;